import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  usePlotArea,
  useXAxisScale,
  useYAxisScale,
} from 'recharts';
import { doc, onSnapshot } from 'firebase/firestore';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { ExternalLink, FileText, LayoutDashboard, Loader2, RefreshCw, SlidersHorizontal, X, AlertTriangle, Box, ChevronRight, Filter } from 'lucide-react';
import { db, storage } from '../../utils/firebase';
import { useAuth } from '../../utils/AuthContext';
import { useCRM, useCRMCollections } from '../../utils/CRMContext';
import { mirrorAllOnboardingTasks } from '../../utils/onboardingTaskBridge';
import {
  applyForecastFlowFilter,
  buildForecastMoneyOutSections,
  flattenForecastMoneyOutSections,
  summarizeMoneyOutFlatRows,
  FORECAST_FLOW_FILTER_OFF,
  MONEY_OUT_FILTER_LABELS,
  moneyOutFilterTagsPresent,
  moneyOutKindFilterActive,
  selectMoneyOutForKindFilter,
  toggleMoneyOutFilterTag,
  forecastFlowFilterIsActive,
  forecastFlowFilterProjectClassState,
  forecastFlowFilterProjectKeyChecked,
  groupForecastItemsByProject,
  listForecastFlowFilterProjects,
  toggleForecastFlowFilterClass,
  toggleForecastFlowFilterProjectKey,
  type ForecastDetailGroup,
  type ForecastFlowFilter,
  type ForecastFlowFilterClass,
  type ForecastFlowFilterProject,
  type MoneyOutFilterTag,
  type ForecastMoneyOutBucket,
  type ForecastMoneyOutEntry,
  type ForecastMoneyOutFlatRow,
  type ForecastMoneyOutSection,
  type ForecastMoneyOutSectionKey,
} from '../../utils/forecastDetailGroups';
import { eventTargetIsEditable } from '../../utils/isEditableTarget';
import {
  forecastBucketAction,
  forecastDetailHref,
  forecastGroupHref,
  forecastItemPeek,
  type ForecastPeek,
} from '../../utils/forecastDetailHref';
import {
  buildForecastQuoteCoverageMarkers,
  forecastQuoteCoverageHubHref,
  forecastQuoteCoverageMarkerLabel,
  groupForecastQuoteCoverageMarkersByMonth,
  quotePlaceholderApSlicesFromMarkers,
  quoteWarnAnchorValue,
  type ForecastQuoteCoverageMarker,
} from '../../utils/financesQuoteCoverageWarnings';
import { resolveEmployeeDesignations } from '../../utils/contractorPayablesAccess';
import {
  bakedApprovedCapexLines,
  CAPITAL_PLAN_STATUS_LABELS,
  selectCapitalPlanOverlay,
  serializeScenarioLines,
  shouldAutosaveCapitalPlan,
  stackedCapexOverlayLines,
  toggleCapitalPlanComparePin,
} from '../../utils/capitalPlans';
import { useCapitalPlanWorkspace } from '../../hooks/useCapitalPlanWorkspace';
import type { RfqProject, RfqSubmission } from '../../types';
import type { SupplierBill, SupplierBillCategory } from '../../utils/supplierBills';
import { approvedStructuresForSupplierOnProject } from '../../utils/rfqApprovedQuoteCoverage';
import { normalizeQuotedStructures } from '../../utils/timberQuoteStreamKey';
import {
  billScheduleSlices,
  HoverTip,
  ScheduleTipContent,
} from '../billing/SupplierBillHover';
import {
  OverheadExpense,
  PayrollPosition,
  PayrollSettings,
  DEFAULT_PAYROLL_SETTINGS,
  CashflowHistoryCache,
  CashflowMethod,
  CashflowMethodData,
  DailyFinanceReport,
  BonusPlan,
  ObligationSettings,
  TaxPlan,
} from '../../types/finances';
import {
  forecastMonthly,
  buildOverlayPoints,
  buildForecastChartTimeline,
  buildCashBalanceByMonthKey,
  averageMonthlyActuals,
  toMonthKey,
  CashflowOverlayPoint,
  ExpenseCategoryBreakdown,
} from '../../utils/financesForecast';
import {
  FORECAST_AVG_DAYS_PER_MONTH,
  FORECAST_DAY_ANCHOR_FRACTION,
  FORECAST_DAY_ZOOM_MONTHS,
  FORECAST_MIN_ZOOM_MONTHS,
  FORECAST_WEEK_ZOOM_MONTHS,
  addLocalDays,
  buildDailyForecastChartSources,
  buildForecastDayBuckets,
  buildWeeklyForecastChartSources,
  dailyChartBarSize,
  dateFromMonthX,
  forecastChartGrainFromPreset,
  forecastNegativeAxisFloor,
  forecastYAxisTicks,
  historicalSequenceChartPoints,
  joinProjectedCashAtNow,
  monotoneXSplinePathD,
  rollDailyWalkToWeeks,
  sequenceCashByDayKey,
  startOfWeekSunday,
  runwayZoomWindow,
  visibleMonthSpan,
  resolveForecastPayrollSchedule,
  walkForecastDailyBalances,
  weekPlotDay,
  xForDay,
  zoomWindowAroundAnchor,
  zoomWindowToDayRange,
  type ForecastChartGrain,
  type ForecastZoomPreset,
} from '../../utils/forecastDailyChart';
import { buildCashFlowProjection, type CashFlowProjectionItem } from '../../utils/cashFlowProjection';
import { buildOverheadBurnByDayKey } from '../../utils/overheadForecastBurn';
import {
  assembleCashFlowProjectionInputs,
  buildAwardedQuoteApFromPage,
} from '../../utils/forecastPageModel';
import {
  applyCashFlowScenario,
  coveredCapexBillIds,
  payNowShiftLines,
  projectionItemKey,
  scenarioHasEffect,
  type ScenarioLine,
} from '../../utils/cashFlowScenario';
import { useCashFlowProjectionData } from '../../hooks/useCashFlowProjectionData';
import { obligationYearsForHorizon, useObligationWorkspace } from '../../hooks/useObligationWorkspace';
import { hintAnnualProfitFromCashflow } from '../../utils/taxPlan';
import { canViewBonusAndTaxPlans } from '../../utils/userRoles';
import { refreshCashflowHistory } from '../../utils/financesApi';
import {
  defaultEmployeeBenefitsSettings,
  subscribeEmployeeBenefitsSettings,
} from '../../utils/peopleHubFirestore';
import {
  DEFAULT_BILL_PAY_CLEARING,
  subscribeBillPayClearingSettings,
  saveBillPayClearingSettings,
} from '../../utils/billPayClearingService';
import CashPlanningModal from './CashPlanningModal';
import CockpitModal from './CockpitModal';

interface ForecastViewProps {
  overhead: OverheadExpense[];
  positions: PayrollPosition[];
  settings?: PayrollSettings;
  canWrite?: boolean;
  canComment?: boolean;
  budgetedOpexMonthly?: number;
  /** Monthly equivalent of each OpEx group that has a budget set. */
  opexBudgetMonthlyByGroup?: Record<string, number>;
}

type Horizon = 12 | 24 | 36;

const HORIZONS: Horizon[] = [12, 24, 36];

/** QuickBooks actuals palette — distinct from the cash-flow bars. */
const ACTUAL_REVENUE_COLOR = '#166534';
const ACTUAL_COGS_COLOR = '#D97706';
const ACTUAL_OVERHEAD_COLOR = '#991B1B';

const ACTUAL_MARKETING_COLOR = '#EC4899';

const ACTUAL_REVENUE_KEY = 'Actual revenue';
const ACTUAL_COGS_KEY = 'Actual COGS';
const ACTUAL_MARKETING_KEY = 'Actual marketing';
const ACTUAL_OVERHEAD_KEY = 'Actual overhead';

const CASH_BALANCE_KEY = 'Cash balance';
const CASH_BALANCE_COLOR = '#0369A1';

const PROJECTED_CASH_KEY = 'Projected cash';
const SCENARIO_CASH_KEY = 'Scenario cash';
/** Always-on series so a day with no bars still opens the tooltip on its cash balance. */
const HOVER_CASH_KEY = 'projHoverCash';
const SCENARIO_CASH_COLOR = '#0F766E';
const PROJECTED_CASH_GHOST = '#94A3B8';

/** Chart geometry shared with the wheel-zoom handler (matches YAxis width / margin). */
const CHART_Y_AXIS_PX = 70;
const CHART_RIGHT_MARGIN_PX = 20;

/** Tooltip-only row fields for the projection breakdown on future months. */
const PROJ_AR_IN = 'projArIn';
const PROJ_AP_OUT = 'projApOut';
const PROJ_BURN = 'projBurn';
const PROJ_PLANNED = 'projPlannedOut';
const PROJ_JOB = 'projJobOut';
const PROJ_ESTIMATED = 'projEstimatedAr';
/** Row → projection month key, so a chart click can open the breakdown. */
const PROJ_MONTH_KEY = 'projMonthKey';
/** Row → YYYY-MM-DD when the chart is in daily grain. */
const PROJ_DAY_KEY = 'projDayKey';
const PROJ_DAY_HAS_DETAIL = 'projDayHasDetail';
/** Row → Sunday YYYY-MM-DD when the chart is in weekly grain. */
const PROJ_WEEK_KEY = 'projWeekKey';

/**
 * Monthly cash-flow bars riding the projected cash line: at each month's
 * point on the dashed line, AR in rises above it while AP out, burn, and
 * planned obligations hang below it as floating [low, high] ranges.
 */
const WF_AR_KEY = 'AR in';
const WF_AP_KEY = 'AP out';
const WF_BURN_KEY = 'Burn';
const WF_PLANNED_KEY = 'Planned';
const WF_JOB_KEY = 'Job costs';
const WF_AR_COLOR = '#16A34A';
const WF_AP_COLOR = '#DC2626';
const WF_BURN_COLOR = '#78716C';
const WF_PLANNED_COLOR = '#B45309';
const WF_JOB_COLOR = '#6D28D9';
const BURN_DELTA_KEY = 'burnDelta';
const BURN_DELTA_MIN = 1;
const BURN_ARROW_MIN_BAR = 10;
/** Chart row: quote-coverage warnings anchored to AP-out months. */
const PROJ_QUOTE_MARKERS = 'quoteCoverageMarkers';
/** AP bar is a pixel sliver only — real AP out stays in PROJ_AP_OUT. */
const WF_AP_WARN_ONLY = 'apWarnSliverOnly';
const AP_WARN_MIN_BAR_PX = 3;
/** Pin one timeline-style caution triangle to the top of the tallest waterfall bar. */
const WF_WARN_KEY = 'quoteWarn';

function BurnBarWithArrow(props: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  fillOpacity?: number | string;
  payload?: ForecastChartRow;
}) {
  const { x, y, width, height, fill, fillOpacity, payload } = props;
  if (
    x == null || y == null || width == null || height == null
    || !Number.isFinite(x) || !Number.isFinite(y)
    || !Number.isFinite(width) || !Number.isFinite(height)
  ) {
    return null;
  }
  const top = Math.min(y, y + height);
  const barH = Math.abs(height);
  const barW = Math.abs(width);
  if (barW < 0.5 || barH < 0.5) return null;
  const delta = Number(payload?.[BURN_DELTA_KEY]);
  const dir = Number.isFinite(delta) && Math.abs(delta) >= BURN_DELTA_MIN
    ? (delta > 0 ? 'up' : 'down')
    : null;
  const cx = x + barW / 2;
  const cy = top + barH / 2;
  return (
    <g className="fin-burn-bar">
      <rect
        x={x}
        y={top}
        width={barW}
        height={barH}
        fill={fill}
        fillOpacity={fillOpacity ?? 0.5}
        rx={1}
      />
      {dir && barH >= BURN_ARROW_MIN_BAR && (
        <g transform={`translate(${cx} ${cy})`} aria-hidden>
          <path
            d={dir === 'up' ? 'M0 3.2 V-2.4 M-2.2 0.2 L0-2.4 L2.2 0.2' : 'M0-3.2 V2.4 M-2.2-0.2 L0 2.4 L2.2-0.2'}
            fill="none"
            stroke="#111111"
            strokeWidth={1}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      )}
    </g>
  );
}

function ApOutBarWithWarnings(props: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  fillOpacity?: number | string;
  payload?: ForecastChartRow;
}) {
  const { x, y, width, height, fill, fillOpacity, payload } = props;
  if (
    x == null || y == null || width == null || height == null
    || !Number.isFinite(x) || !Number.isFinite(y)
    || !Number.isFinite(width) || !Number.isFinite(height)
  ) {
    return null;
  }
  const warnOnly = payload?.[WF_AP_WARN_ONLY] === true;
  let top = Math.min(y, y + height);
  let barH = Math.abs(height);
  const barW = Math.abs(width);
  if (barW < 0.5) return null;
  if (warnOnly && barH < AP_WARN_MIN_BAR_PX) {
    top = top + barH - AP_WARN_MIN_BAR_PX;
    barH = AP_WARN_MIN_BAR_PX;
  }
  if (barH < 0.5 && !warnOnly) return null;

  return (
    <g className="fin-ap-bar">
      <rect
        x={x}
        y={top}
        width={barW}
        height={Math.max(barH, warnOnly ? AP_WARN_MIN_BAR_PX : barH)}
        fill={fill}
        fillOpacity={fillOpacity ?? 0.55}
        rx={1}
      />
    </g>
  );
}

function QuoteWarnMark(props: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: ForecastChartRow;
}) {
  const { x, y, width, height, payload } = props;
  if (
    x == null || y == null || width == null
    || !Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width)
  ) {
    return null;
  }
  if (quoteMarkersFromRow(payload).length === 0) return null;
  const barW = Math.abs(width);
  const top = height == null || !Number.isFinite(height) ? y : Math.min(y, y + height);
  const cx = x + barW / 2;
  return (
    <g className="fin-quote-warn-mark" transform={`translate(${cx} ${top})`} aria-hidden>
      <g transform="translate(-6 -12)">
        <AlertTriangle size={12} strokeWidth={2.4} color="#CA8A04" />
      </g>
    </g>
  );
}

/**
 * Bill → structures it covers. Resolved from the RFQ submission behind the
 * bill so the forecast rows can show *which* structure a timber/CNC bill is
 * for without threading submissions through every list component.
 */
const ForecastBillMetaContext = React.createContext<{
  structuresForBill: (bill: SupplierBill) => string[];
}>({ structuresForBill: () => [] });

const BILL_CATEGORY_BADGE: Partial<Record<SupplierBillCategory, string>> = {
  timber: 'Timber',
  cnc: 'CNC',
  fasteners: 'Fasteners',
  hardware: 'Hardware',
  tooling: 'Tooling',
  freight: 'Freight',
};

const ForecastItemMeta: React.FC<{
  item: CashFlowProjectionItem;
  bill?: SupplierBill;
  hideProjectName?: boolean;
}> = ({ item, bill, hideProjectName }) => {
  const { structuresForBill } = React.useContext(ForecastBillMetaContext);
  const category = bill ? BILL_CATEGORY_BADGE[bill.category] : undefined;
  const structures = bill ? structuresForBill(bill) : (item.structureNames ?? []);
  const projectName = hideProjectName ? undefined : item.projectName;
  if (!projectName && !item.anchor && !category && structures.length === 0) return null;
  return (
    <div className="fin-proj-detail-badges">
      {category && (
        <span className={`fin-proj-detail-kind-badge fin-proj-detail-kind-badge--${bill!.category}`}>{category}</span>
      )}
      {structures.map((name) => (
        <span key={name} className="fin-proj-detail-structure-badge" title={`Structure: ${name}`}>
          <Box size={10} strokeWidth={2.2} aria-hidden />
          {name}
        </span>
      ))}
      {projectName && (
        <span className="fin-proj-detail-project-badge">{projectName}</span>
      )}
      {item.anchor && (
        <div className="fin-proj-detail-anchor">{item.anchor}</div>
      )}
    </div>
  );
};

type ForecastChartRow = Record<
  string,
  | number
  | string
  | null
  | boolean
  | number[]
  | ExpenseCategoryBreakdown[]
  | ForecastQuoteCoverageMarker[]
  | undefined
> & {
  label: string;
  /** Numeric month index — lets the X axis zoom in fractions of a month. */
  x?: number;
  expenseBreakdown?: ExpenseCategoryBreakdown[];
};

type ForecastPeriodChartSource = {
  label: string;
  x: number;
  monthKey: string;
  dayKey?: string;
  weekKey?: string;
  actualCash: number | null;
  projectedCash: number | null;
  scenarioCash: number | null;
  arIn: number;
  apOut: number;
  plannedOut: number;
  jobOut: number;
  burn: number;
  hasEstimatedAr: boolean;
  quoteMarkers: ForecastQuoteCoverageMarker[];
  hasDetail: boolean;
};

function withForecastFlowFilter(
  src: ForecastPeriodChartSource,
  items: CashFlowProjectionItem[] | undefined,
  filter: ForecastFlowFilter,
): ForecastPeriodChartSource {
  if (!forecastFlowFilterIsActive(filter)) return src;
  const visible = applyForecastFlowFilter({
    items: items ?? [],
    burn: src.burn,
    quoteMarkers: src.quoteMarkers,
    filter,
  });
  return {
    ...src,
    arIn: visible.arIn,
    apOut: visible.apOut,
    plannedOut: visible.plannedOut,
    jobOut: visible.jobOut,
    burn: visible.burn,
    quoteMarkers: visible.quoteMarkers,
    hasEstimatedAr: Boolean(src.hasEstimatedAr && visible.arIn > 0),
  };
}

function stripChartRowCash(row: ForecastChartRow): ForecastChartRow {
  return {
    ...row,
    [CASH_BALANCE_KEY]: null,
  };
}

function historicalCashOverlayRows(
  points: ReturnType<typeof historicalSequenceChartPoints>,
): ForecastChartRow[] {
  return points.map((src) => {
    const row: ForecastChartRow = { label: src.label, x: src.x };
    row[CASH_BALANCE_KEY] = src.actualCash;
    row[PROJ_DAY_KEY] = src.dayKey;
    return row;
  });
}

function ForecastScalePath({
  points,
  stroke,
  strokeWidth,
  strokeDasharray,
  clipId,
  smooth,
}: {
  points: Array<{ x: number; y: number }>;
  stroke: string;
  strokeWidth: number;
  strokeDasharray?: string;
  clipId: string;
  smooth?: boolean;
}) {
  const xScale = useXAxisScale();
  const yScale = useYAxisScale();
  const plot = usePlotArea();
  if (!xScale || !yScale || !plot || points.length < 2) return null;
  const mapped: Array<{ x: number; y: number }> = [];
  points.forEach((pt) => {
    const px = xScale(pt.x);
    const py = yScale(pt.y);
    if (px == null || py == null || !Number.isFinite(px) || !Number.isFinite(py)) return;
    mapped.push({ x: px, y: py });
  });
  if (mapped.length < 2) return null;
  const d = smooth
    ? monotoneXSplinePathD(mapped)
    : mapped.map((pt, i) => `${i ? 'L' : 'M'}${pt.x} ${pt.y}`).join(' ');
  if (!d) return null;
  return (
    <g pointerEvents="none">
      <defs>
        <clipPath id={clipId}>
          <rect x={plot.x} y={plot.y} width={plot.width} height={plot.height} />
        </clipPath>
      </defs>
      <path
        clipPath={`url(#${clipId})`}
        d={d}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeDasharray={strokeDasharray}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </g>
  );
}

/** Light red hash across the whole plot, from zero down to the axis floor. */
function NegativeCashBand({ floor }: { floor: number }) {
  const yScale = useYAxisScale();
  const plot = usePlotArea();
  const hatchId = `forecast-neg-hatch-${useId().replace(/:/g, '')}`;
  if (!yScale || !plot || !(floor < 0)) return null;
  const yZero = yScale(0);
  if (yZero == null || !Number.isFinite(Number(yZero))) return null;
  const plotBottom = plot.y + plot.height;
  const top = Math.max(plot.y, Math.min(Number(yZero), plotBottom));
  const height = plotBottom - top;
  if (height < 1 || plot.width < 1) return null;
  return (
    <g pointerEvents="none">
      <defs>
        <pattern id={hatchId} patternUnits="userSpaceOnUse" width={8} height={8}>
          <path
            d="M-2 2 L2 -2 M0 8 L8 0 M6 10 L10 6"
            stroke="#DC2626"
            strokeOpacity={0.22}
            strokeWidth={1.15}
          />
        </pattern>
      </defs>
      <rect
        x={plot.x}
        y={top}
        width={plot.width}
        height={height}
        fill="rgba(220, 38, 38, 0.03)"
        pointerEvents="none"
      />
      <rect
        x={plot.x}
        y={top}
        width={plot.width}
        height={height}
        fill={`url(#${hatchId})`}
        pointerEvents="none"
      />
    </g>
  );
}

type HistoricCashHover = {
  dayKey: string;
  label: string;
  cash: number;
  clientX: number;
  clientY: number;
};

function nearestHistoricCashPoint(
  points: ForecastChartRow[],
  xScale: (value: number) => number | undefined,
  pixelX: number,
): Omit<HistoricCashHover, 'clientX' | 'clientY'> | null {
  let bestIndex = -1;
  let bestDist = Infinity;
  for (let i = 0; i < points.length; i += 1) {
    const row = points[i];
    if (typeof row.x !== 'number') continue;
    const px = xScale(row.x);
    if (px == null || !Number.isFinite(px)) continue;
    const dist = Math.abs(px - pixelX);
    if (dist < bestDist) {
      bestDist = dist;
      bestIndex = i;
    }
  }
  const best = bestIndex >= 0 ? points[bestIndex] : null;
  if (!best || typeof best[CASH_BALANCE_KEY] !== 'number') return null;
  const dayKey = typeof best[PROJ_DAY_KEY] === 'string' ? best[PROJ_DAY_KEY] : best.label;
  return { dayKey, label: best.label, cash: Number(best[CASH_BALANCE_KEY]) };
}

/** Draws Sequence daily cash using the chart scales without adding bar-layout rows. */
function HistoricDailyCashPath({
  points,
  onHover,
}: {
  points: ForecastChartRow[];
  onHover: (hover: HistoricCashHover | null) => void;
}) {
  const xScale = useXAxisScale();
  const mapped: Array<{ x: number; y: number }> = [];
  points.forEach((row) => {
    const cash = row[CASH_BALANCE_KEY];
    if (typeof row.x !== 'number' || typeof cash !== 'number') return;
    mapped.push({ x: row.x, y: cash });
  });
  const hoverAt = (pixelX: number, clientX: number, clientY: number) => {
    if (!xScale) return;
    const hit = nearestHistoricCashPoint(points, (value) => {
      const px = xScale(value);
      return typeof px === 'number' ? px : undefined;
    }, pixelX);
    onHover(hit ? { ...hit, clientX, clientY } : null);
  };
  return (
    <g className="fin-forecast-historic-cash">
      <ForecastScalePath
        points={mapped}
        stroke={CASH_BALANCE_COLOR}
        strokeWidth={2.2}
        clipId="forecast-historic-cash-clip"
      />
      {xScale && mapped.length > 1 && (
        <HistoricCashHitPath
          points={mapped}
          xScale={xScale}
          onMove={hoverAt}
          onLeave={() => onHover(null)}
        />
      )}
    </g>
  );
}

function HistoricCashHitPath({
  points,
  xScale,
  onMove,
  onLeave,
}: {
  points: Array<{ x: number; y: number }>;
  xScale: (value: number) => number | undefined;
  onMove: (pixelX: number, clientX: number, clientY: number) => void;
  onLeave: () => void;
}) {
  const yScale = useYAxisScale();
  const plot = usePlotArea();
  if (!yScale || !plot) return null;
  const cmds: string[] = [];
  points.forEach((pt) => {
    const px = xScale(pt.x);
    const py = yScale(pt.y);
    if (px == null || py == null || !Number.isFinite(px) || !Number.isFinite(py)) return;
    cmds.push(`${cmds.length ? 'L' : 'M'}${px} ${py}`);
  });
  if (cmds.length < 2) return null;
  return (
    <path
      d={cmds.join(' ')}
      fill="none"
      stroke="transparent"
      strokeWidth={16}
      clipPath="url(#forecast-historic-cash-clip)"
      style={{ pointerEvents: 'stroke' }}
      onMouseMove={(e) => {
        const svg = e.currentTarget.ownerSVGElement;
        if (!svg) return;
        const rect = svg.getBoundingClientRect();
        onMove(e.clientX - rect.left, e.clientX, e.clientY);
      }}
      onMouseLeave={onLeave}
    />
  );
}

function ForecastCashOverlays({
  historicPoints,
  projectedPoints,
  scenarioPoints,
  showHistoric,
  overlayProjected,
  scenarioActive,
  onHistoricHover,
}: {
  historicPoints: ForecastChartRow[];
  projectedPoints: Array<{ x: number; y: number }>;
  scenarioPoints: Array<{ x: number; y: number }>;
  showHistoric: boolean;
  overlayProjected: boolean;
  scenarioActive: boolean;
  onHistoricHover: (hover: HistoricCashHover | null) => void;
}) {
  return (
    <>
      {showHistoric && <HistoricDailyCashPath points={historicPoints} onHover={onHistoricHover} />}
      {overlayProjected && (
        <ForecastScalePath
          points={projectedPoints}
          stroke={scenarioActive ? PROJECTED_CASH_GHOST : CASH_BALANCE_COLOR}
          strokeWidth={scenarioActive ? 1.6 : 2.2}
          strokeDasharray="6 4"
          clipId="forecast-projected-cash-clip"
          smooth
        />
      )}
      {overlayProjected && scenarioActive && (
        <ForecastScalePath
          points={scenarioPoints}
          stroke={SCENARIO_CASH_COLOR}
          strokeWidth={2.4}
          clipId="forecast-scenario-cash-clip"
          smooth
        />
      )}
    </>
  );
}

function mapPeriodSourceToChartRow(
  src: ForecastPeriodChartSource,
  scenarioActive: boolean,
  includeBurnBar: boolean,
): ForecastChartRow {
  const row: ForecastChartRow = { label: src.label, x: src.x };
  row[ACTUAL_REVENUE_KEY] = null;
  row[ACTUAL_COGS_KEY] = null;
  row[ACTUAL_MARKETING_KEY] = null;
  row[ACTUAL_OVERHEAD_KEY] = null;
  row[CASH_BALANCE_KEY] = src.actualCash;
  row[PROJECTED_CASH_KEY] = src.projectedCash;
  row[SCENARIO_CASH_KEY] = src.scenarioCash;
  const hoverCash = src.actualCash ?? (scenarioActive ? src.scenarioCash ?? src.projectedCash : src.projectedCash);
  if (hoverCash != null) row[HOVER_CASH_KEY] = hoverCash;
  row[PROJ_AR_IN] = src.arIn;
  row[PROJ_AP_OUT] = src.apOut;
  row[PROJ_BURN] = src.burn;
  row[PROJ_PLANNED] = src.plannedOut;
  row[PROJ_JOB] = src.jobOut;
  row[PROJ_ESTIMATED] = src.hasEstimatedAr ? 1 : 0;
  row[PROJ_MONTH_KEY] = src.monthKey;
  if (src.dayKey) row[PROJ_DAY_KEY] = src.dayKey;
  if (src.weekKey) row[PROJ_WEEK_KEY] = src.weekKey;
  if (src.hasDetail) row[PROJ_DAY_HAS_DETAIL] = true;
  const lineValue = Number(scenarioActive ? src.scenarioCash : src.projectedCash) || 0;
  const burnOff = includeBurnBar ? src.burn : 0;
  if (src.arIn > 0) row[WF_AR_KEY] = [lineValue, lineValue + src.arIn];
  if (src.quoteMarkers.length) row[PROJ_QUOTE_MARKERS] = src.quoteMarkers;
  if (src.apOut > 0) {
    row[WF_AP_KEY] = [lineValue - src.apOut, lineValue];
  } else if (src.quoteMarkers.length) {
    row[WF_AP_KEY] = [lineValue - 1, lineValue];
    row[WF_AP_WARN_ONLY] = true;
  }
  if (includeBurnBar && src.burn > 0) {
    row[WF_BURN_KEY] = [lineValue - src.apOut - src.burn, lineValue - src.apOut];
  }
  if (src.plannedOut > 0) {
    row[WF_PLANNED_KEY] = [
      lineValue - src.apOut - burnOff - src.plannedOut,
      lineValue - src.apOut - burnOff,
    ];
  }
  if (src.jobOut > 0) {
    row[WF_JOB_KEY] = [
      lineValue - src.apOut - burnOff - src.plannedOut - src.jobOut,
      lineValue - src.apOut - burnOff - src.plannedOut,
    ];
  }
  if (src.quoteMarkers.length) {
    const peak = quoteWarnAnchorValue(
      [row[WF_AR_KEY], row[WF_AP_KEY], row[WF_BURN_KEY], row[WF_PLANNED_KEY], row[WF_JOB_KEY]],
      lineValue,
    );
    row[WF_WARN_KEY] = [peak, peak];
  }
  return row;
}

const FLOW_FILTER_OPTIONS: Array<{ key: ForecastFlowFilterClass; label: string }> = [
  { key: 'project', label: 'Project' },
  { key: 'capex', label: 'Capex' },
  { key: 'overhead', label: 'Overhead' },
];

function ForecastFlowFilterControl({
  filter,
  onChange,
  projects,
  onExportProject,
  exportingKey,
}: {
  filter: ForecastFlowFilter;
  onChange: (next: ForecastFlowFilter) => void;
  projects: ForecastFlowFilterProject[];
  onExportProject?: (project: ForecastFlowFilterProject) => void;
  exportingKey?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const active = forecastFlowFilterIsActive(filter);
  const allKeys = useMemo(() => projects.map((row) => row.key), [projects]);
  const projectState = forecastFlowFilterProjectClassState(filter, allKeys);
  const q = query.trim().toLowerCase();
  const visibleJobs = q
    ? projects.filter((row) => row.label.toLowerCase().includes(q))
    : projects;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (wrapRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="fin-flow-filter" ref={wrapRef}>
      <button
        type="button"
        className={`fin-flow-filter-btn${active ? ' is-on' : ''}${open ? ' is-open' : ''}`}
        aria-label={active ? 'Filter cash-flow bars (on)' : 'Filter cash-flow bars'}
        aria-haspopup="true"
        aria-expanded={open}
        title="Filter bars"
        onClick={() => {
          setOpen((prev) => {
            const next = !prev;
            if (next) setQuery('');
            return next;
          });
        }}
      >
        <Filter size={13} strokeWidth={2} />
      </button>
      {open && (
        <div className="fin-flow-filter-menu" role="group" aria-label="Show on bars">
          {FLOW_FILTER_OPTIONS.map((opt) => {
            const classOn = opt.key === 'project' ? projectState !== 'off' : filter[opt.key];
            const mixed = opt.key === 'project' && projectState === 'mixed';
            return (
              <React.Fragment key={opt.key}>
                <button
                  type="button"
                  className={`fin-flow-filter-opt${classOn ? ' is-on' : ''}`}
                  aria-pressed={
                    opt.key === 'project'
                      ? (projectState === 'mixed' ? 'mixed' : projectState === 'on')
                      : filter[opt.key]
                  }
                  onClick={() => onChange(toggleForecastFlowFilterClass(filter, opt.key))}
                >
                  <span
                    className={`fin-flow-filter-box${classOn ? ' is-on' : ''}${mixed ? ' is-mixed' : ''}`}
                    aria-hidden
                  />
                  {opt.label}
                </button>
                {opt.key === 'project' && projects.length > 0 && (
                  <div className="fin-flow-filter-jobs">
                    {projects.length > 6 && (
                      <input
                        className="fin-flow-filter-search"
                        type="search"
                        value={query}
                        placeholder="Find a job…"
                        aria-label="Find a job"
                        onChange={(event) => setQuery(event.target.value)}
                      />
                    )}
                    <div className="fin-flow-filter-job-list" role="group" aria-label="Projects">
                      {visibleJobs.length === 0 ? (
                        <div className="fin-flow-filter-empty">No matching jobs</div>
                      ) : visibleJobs.map((job) => {
                        const checked = forecastFlowFilterProjectKeyChecked(filter, job.key);
                        const exporting = exportingKey === job.key;
                        return (
                          <div key={job.key} className="fin-flow-filter-job-row">
                            <button
                              type="button"
                              className={`fin-flow-filter-opt is-job${checked ? ' is-on' : ''}`}
                              aria-pressed={checked}
                              onClick={() => onChange(toggleForecastFlowFilterProjectKey(filter, job.key, allKeys))}
                            >
                              <span className={`fin-flow-filter-box${checked ? ' is-on' : ''}`} aria-hidden />
                              <span className="fin-flow-filter-job-label">{job.label}</span>
                            </button>
                            {onExportProject && (
                              <button
                                type="button"
                                className="fin-flow-filter-export"
                                aria-label={`Export ${job.label} cashflow PDF`}
                                title="Export cashflow PDF"
                                disabled={exporting}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onExportProject(job);
                                }}
                              >
                                {exporting
                                  ? <Loader2 size={11} className="fin-flow-filter-export-spin" />
                                  : <FileText size={11} />}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
          {active && (
            <button
              type="button"
              className="fin-flow-filter-clear"
              onClick={() => {
                onChange(FORECAST_FLOW_FILTER_OFF);
                setOpen(false);
              }}
            >
              Show all
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function MoneyOutKindFilter({
  tags,
  present,
  onChange,
}: {
  tags: readonly MoneyOutFilterTag[];
  present: readonly MoneyOutFilterTag[];
  onChange: (next: MoneyOutFilterTag[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const active = moneyOutKindFilterActive(tags);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (wrapRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (present.length === 0) return null;

  return (
    <div className="fin-flow-filter" ref={wrapRef}>
      <button
        type="button"
        className={`fin-flow-filter-btn${active ? ' is-on' : ''}${open ? ' is-open' : ''}`}
        aria-label={active ? 'Filter money out (on)' : 'Filter money out'}
        aria-haspopup="true"
        aria-expanded={open}
        title="Filter money out"
        onClick={() => setOpen((prev) => !prev)}
      >
        <Filter size={13} strokeWidth={2} />
      </button>
      {open && (
        <div className="fin-flow-filter-menu" role="group" aria-label="Show in money out">
          {present.map((tag) => {
            const on = tags.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                className={`fin-flow-filter-opt${on ? ' is-on' : ''}`}
                aria-pressed={on}
                onClick={() => onChange(toggleMoneyOutFilterTag(tags, tag))}
              >
                <span className={`fin-flow-filter-box${on ? ' is-on' : ''}`} aria-hidden />
                {MONEY_OUT_FILTER_LABELS[tag]}
              </button>
            );
          })}
          {active && (
            <button
              type="button"
              className="fin-flow-filter-clear"
              onClick={() => {
                onChange([]);
                setOpen(false);
              }}
            >
              Show all
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function isQuoteCoverageMarker(value: unknown): value is ForecastQuoteCoverageMarker {
  if (!value || typeof value !== 'object') return false;
  const m = value as ForecastQuoteCoverageMarker;
  return typeof m.monthKey === 'string' && typeof m.dealId === 'string' && (m.kind === 'timber' || m.kind === 'cnc');
}

function quoteMarkersFromRow(row: ForecastChartRow | undefined): ForecastQuoteCoverageMarker[] {
  const raw = row?.[PROJ_QUOTE_MARKERS];
  if (!Array.isArray(raw)) return [];
  const out: ForecastQuoteCoverageMarker[] = [];
  for (const item of raw as unknown[]) {
    if (isQuoteCoverageMarker(item)) out.push(item);
  }
  return out;
}

interface ForecastChartTooltipProps {
  active?: boolean;
  label?: string | number;
  payload?: Array<{
    name?: string;
    value?: number | string | null;
    color?: string;
    dataKey?: string | number;
    payload?: ForecastChartRow;
  }>;
  formatLabel?: (x: number) => string;
  historicHover?: HistoricCashHover | null;
}

function forecastTooltipTitle(
  row: ForecastChartRow | undefined,
  label: string | number | undefined,
  formatLabel?: (x: number) => string,
): string {
  if (typeof row?.label === 'string' && row.label.trim()) return row.label;
  if (typeof label === 'number' && Number.isFinite(label)) {
    return formatLabel?.(label) || '';
  }
  if (typeof label === 'string' && label.trim() && !/^\d+\.\d+$/.test(label)) return label;
  const asNum = typeof label === 'string' ? Number(label) : NaN;
  if (Number.isFinite(asNum) && formatLabel) return formatLabel(asNum);
  return '';
}

const ForecastChartTooltip: React.FC<ForecastChartTooltipProps> = ({
  active,
  label,
  payload,
  formatLabel,
  historicHover,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<'above' | 'below'>('above');

  const row = payload?.[0]?.payload;
  const entries = (payload ?? []).filter(
    p =>
      p.value != null &&
      typeof p.value === 'number' &&
      !Number.isNaN(p.value) &&
      // Flow bars are already itemized in the projection block below.
      p.name !== WF_AR_KEY && p.name !== WF_AP_KEY && p.name !== WF_BURN_KEY && p.name !== WF_PLANNED_KEY && p.name !== WF_JOB_KEY && p.name !== WF_WARN_KEY &&
      // Drawn on the projection block so a hidden overlay line still shows its balance.
      p.name !== PROJECTED_CASH_KEY && p.name !== SCENARIO_CASH_KEY &&
      p.dataKey !== HOVER_CASH_KEY && p.name !== HOVER_CASH_KEY,
  );

  useLayoutEffect(() => {
    const el = ref.current;
    if (!active || !el) return;

    const wrapper = el.closest('.recharts-tooltip-wrapper') as HTMLElement | null;
    if (!wrapper) return;

    // Measure the unclamped recharts position, then write the clamp onto the
    // node directly. Keeping this off React state avoids a setState loop.
    el.style.transform = 'none';

    const margin = 8;
    const gap = 10;
    const headerBottom =
      document.querySelector('.app-header')?.getBoundingClientRect().bottom ?? 0;
    const topLimit = Math.max(margin, headerBottom + margin);
    const rect = wrapper.getBoundingClientRect();
    const height = el.offsetHeight;
    const width = el.offsetWidth;

    const spaceAbove = rect.top - topLimit;
    const spaceBelow = window.innerHeight - rect.top - margin;
    const next: 'above' | 'below' =
      height + gap <= spaceAbove || spaceAbove >= spaceBelow ? 'above' : 'below';

    const desiredTop = next === 'above' ? rect.top - gap - height : rect.top + gap;
    const maxTop = window.innerHeight - margin - height;
    const clampedTop = Math.min(Math.max(desiredTop, topLimit), Math.max(topLimit, maxTop));

    const desiredLeft = rect.left;
    const maxLeft = window.innerWidth - margin - width;
    const clampedLeft = Math.min(Math.max(desiredLeft, margin), Math.max(margin, maxLeft));
    const nextX = clampedLeft - desiredLeft;
    const nextY = clampedTop - desiredTop;

    el.style.transform =
      next === 'above'
        ? `translate(${nextX}px, calc(-100% - 10px + ${nextY}px))`
        : `translate(${nextX}px, ${10 + nextY}px)`;

    setPlacement((prev) => (prev === next ? prev : next));
  }, [active, label, payload, historicHover]);

  if (historicHover) return null;
  if (!active || !payload?.length) return null;

  const projectedCash = typeof row?.[PROJECTED_CASH_KEY] === 'number' ? Number(row[PROJECTED_CASH_KEY]) : null;
  const scenarioCash = typeof row?.[SCENARIO_CASH_KEY] === 'number' ? Number(row[SCENARIO_CASH_KEY]) : null;
  const hoverCash = typeof row?.[HOVER_CASH_KEY] === 'number' ? Number(row[HOVER_CASH_KEY]) : null;
  const actualCash = typeof row?.[CASH_BALANCE_KEY] === 'number'
    ? Number(row[CASH_BALANCE_KEY])
    : (hoverCash != null && hoverCash !== projectedCash && hoverCash !== scenarioCash ? hoverCash : null);
  const burnCash = typeof row?.[PROJ_BURN] === 'number' ? Number(row[PROJ_BURN]) : 0;
  const itemized = [PROJ_AR_IN, PROJ_AP_OUT, PROJ_PLANNED, PROJ_JOB]
    .reduce((sum, key) => sum + (typeof row?.[key] === 'number' ? Number(row[key]) : 0), 0);
  const nothingElse = itemized === 0 && quoteMarkersFromRow(row).length === 0;
  if (nothingElse) {
    if (actualCash == null && projectedCash == null && scenarioCash == null && burnCash <= 0) return null;
    return (
      <div
        ref={ref}
        className={`fin-forecast-chart-tooltip fin-forecast-chart-tooltip--${placement}`}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          {forecastTooltipTitle(row, label, formatLabel)}
        </div>
        {actualCash != null && (
          <div style={{ fontSize: 13, fontWeight: 600, color: CASH_BALANCE_COLOR }}>
            Cash balance: {formatCurrency(actualCash)}
          </div>
        )}
        {projectedCash != null && (
          <div style={{ fontSize: 13, fontWeight: 600, color: CASH_BALANCE_COLOR, marginTop: actualCash != null ? 2 : 0 }}>
            Projected cash: {formatCurrency(projectedCash)}
          </div>
        )}
        {scenarioCash != null && (
          <div style={{ fontSize: 13, fontWeight: 600, color: SCENARIO_CASH_COLOR, marginTop: 2 }}>
            Scenario cash: {formatCurrency(scenarioCash)}
          </div>
        )}
        {burnCash > 0 && (
          <div style={{ fontSize: 11, color: '#44403C', marginTop: 4 }}>
            Burn: −{formatCurrency(burnCash)}
          </div>
        )}
      </div>
    );
  }

  const canOpenDetail = typeof row?.[PROJ_DAY_KEY] === 'string' || typeof row?.[PROJ_WEEK_KEY] === 'string'
    ? row?.[PROJ_DAY_HAS_DETAIL] === true
    : typeof row?.[PROJ_MONTH_KEY] === 'string';

  return (
    <div
      ref={ref}
      className={`fin-forecast-chart-tooltip fin-forecast-chart-tooltip--${placement}`}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        {forecastTooltipTitle(row, label, formatLabel)}
      </div>
      {entries.map(entry => (
        <div key={String(entry.dataKey)} style={{ color: entry.color, marginBottom: 2 }}>
          {entry.name}: {formatCurrency(Number(entry.value))}
        </div>
      ))}
      {row && typeof row[PROJ_AR_IN] === 'number' && (
        <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px solid #E2E0DB' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6B6960' }}>
            Cash flow projection
          </div>
          {typeof row[PROJECTED_CASH_KEY] === 'number' && (
            <div style={{ fontSize: 13, fontWeight: 600, color: CASH_BALANCE_COLOR, marginTop: 4 }}>
              Projected cash: {formatCurrency(Number(row[PROJECTED_CASH_KEY]))}
            </div>
          )}
          {typeof row[SCENARIO_CASH_KEY] === 'number' && (
            <div style={{ fontSize: 13, fontWeight: 600, color: SCENARIO_CASH_COLOR, marginTop: 2 }}>
              Scenario cash: {formatCurrency(Number(row[SCENARIO_CASH_KEY]))}
            </div>
          )}
          <div style={{ fontSize: 11, color: '#166534', marginTop: 2 }}>
            AR in: +{formatCurrency(Number(row[PROJ_AR_IN]))}
          </div>
          <div style={{ fontSize: 11, color: '#991B1B', marginTop: 2 }}>
            AP out: −{formatCurrency(Number(row[PROJ_AP_OUT] ?? 0))}
          </div>
          <div style={{ fontSize: 11, color: '#44403C', marginTop: 2 }}>
            Burn: −{formatCurrency(Number(row[PROJ_BURN] ?? 0))}
            {typeof row[BURN_DELTA_KEY] === 'number' && Math.abs(Number(row[BURN_DELTA_KEY])) >= BURN_DELTA_MIN && (
              <>
                {' · '}
                {Number(row[BURN_DELTA_KEY]) > 0 ? '↑' : '↓'}
                {' '}
                {formatCurrency(Math.abs(Number(row[BURN_DELTA_KEY])))}
                {' vs prior month'}
              </>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#B45309', marginTop: 2 }}>
            Planned: −{formatCurrency(Number(row[PROJ_PLANNED] ?? 0))}
          </div>
          <div style={{ fontSize: 11, color: '#6D28D9', marginTop: 2 }}>
            Job costs: −{formatCurrency(Number(row[PROJ_JOB] ?? 0))}
          </div>
          {row[PROJ_ESTIMATED] === 1 && (
            <div style={{ fontSize: 10, color: '#92400E', marginTop: 3 }}>
              Includes AR with estimated timing
            </div>
          )}
          {canOpenDetail && (
            <div style={{ fontSize: 10, color: '#6B6960', marginTop: 4 }}>
              {typeof row[PROJ_WEEK_KEY] === 'string'
                ? 'Click the week for the itemized breakdown'
                : typeof row[PROJ_DAY_KEY] === 'string'
                  ? 'Click the day for the itemized breakdown'
                  : 'Click the month for the itemized breakdown'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

const formatAxisCurrency = (value: number) => {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}k`;
  return `${sign}$${Math.round(abs)}`;
};

function formatDayKey(dateKey: string): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  if (!year || !month || !day) return dateKey;
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

const formatDateTime = (d: Date | null): string => {
  if (!d) return '';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

function ForecastAmountTip({
  amount,
  out,
  bill,
  slices,
  peek,
}: {
  amount: number;
  out: boolean;
  bill?: SupplierBill;
  slices: ReturnType<typeof billScheduleSlices>;
  peek: ForecastPeek | null;
}) {
  const amt = (
    <span className={`fin-proj-detail-amt ${out ? 'fin-proj-detail-amt--out' : 'fin-proj-detail-amt--in'}${peek ? ' fin-proj-detail-amt--peekable' : ''}`}>
      {out ? '−' : '+'}{formatCurrency(amount)}
    </span>
  );
  if (!bill && !peek) return amt;
  return (
    <HoverTip
      wide
      tip={
        <>
          {bill && <ScheduleTipContent bill={bill} slices={slices} />}
          {peek && (
            <>
              {bill && <div className="bp-tip-divider" />}
              <span className="bp-tip-hint">
                Press <kbd>Space</kbd>
                {peek.kind === 'bill' ? ' to preview the bill' : ' to preview'}
              </span>
            </>
          )}
        </>
      }
    >
      {amt}
    </HoverTip>
  );
}

function openForecastTarget(href: string, event: { metaKey: boolean; ctrlKey: boolean }, navigate: (to: string) => void) {
  if (event.metaKey || event.ctrlKey) {
    window.open(href, '_blank', 'noopener');
    return;
  }
  navigate(href);
}

function keepPeekWhileInside(event: React.MouseEvent<HTMLElement>, clear: () => void) {
  const next = event.relatedTarget;
  if (next instanceof Node && event.currentTarget.contains(next)) return;
  clear();
}

function payNowToggleState(item: CashFlowProjectionItem, today: Date): 'on' | 'off' | null {
  if (item.payNowKey) return 'on';
  if (item.kind !== 'ap' || item.scenario || !item.dueDate) return null;
  const due = new Date(item.dueDate.getFullYear(), item.dueDate.getMonth(), item.dueDate.getDate());
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return due.getTime() > start.getTime() ? 'off' : null;
}

function dueLabel(item: CashFlowProjectionItem): string {
  if (!item.dueDate) {
    return item.kind === 'planned' ? 'no due date' : 'no due date — counted now';
  }
  return `due ${item.dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

function plannedKindLabel(item: CashFlowProjectionItem, canViewObligations: boolean): string {
  if (canViewObligations && item.plannedKind === 'bonus') return 'Bonus';
  if (canViewObligations && item.plannedKind === 'tax') return 'Tax';
  if (item.scenario) return 'CapEx';
  return 'Planned';
}

function ForecastGroupBadge({
  label,
  sectionKey,
}: {
  label: string;
  sectionKey: ForecastMoneyOutSectionKey;
}) {
  return (
    <span className={`fin-proj-detail-group-badge fin-proj-detail-group-badge--${sectionKey}`} title={label}>
      {label}
    </span>
  );
}

function flatRowGroupBadge(row: ForecastMoneyOutFlatRow): string | undefined {
  if (row.kind === 'item') {
    const title = row.item.plannedKind === 'tax' ? row.item.label : row.item.projectLabel;
    if (row.groupLabel === title) return undefined;
    return row.groupLabel;
  }
  if (row.kind === 'quote') return row.groupLabel;
  if (row.groupLabel === row.label) return undefined;
  return row.groupLabel;
}

function ForecastDetailItemRow({
  item,
  direction,
  bill,
  slices,
  hasPreview,
  onPreviewHover,
  canViewObligations,
  onOpenCockpit,
  payNowToday,
  onTogglePayNow,
  groupBadge,
  groupSectionKey,
}: {
  item: CashFlowProjectionItem;
  direction: 'in' | 'out';
  bill?: SupplierBill;
  slices?: ReturnType<typeof billScheduleSlices>;
  hasPreview?: boolean;
  onPreviewHover?: (peek: ForecastPeek | null) => void;
  canViewObligations: boolean;
  onOpenCockpit?: () => void;
  payNowToday?: Date;
  onTogglePayNow?: (item: CashFlowProjectionItem) => void;
  groupBadge?: string;
  groupSectionKey?: ForecastMoneyOutSectionKey;
}) {
  const navigate = useNavigate();
  const out = direction === 'out';
  const href = forecastDetailHref(item);
  const openCockpit = Boolean(
    !href
    && onOpenCockpit
    && canViewObligations
    && item.kind === 'planned'
    && (item.plannedKind === 'bonus' || item.plannedKind === 'tax'),
  );
  const isTarget = Boolean(href || openCockpit);
  const peek = forecastItemPeek(item, { billHasFile: !!hasPreview });
  const payNow = payNowToday && onTogglePayNow ? payNowToggleState(item, payNowToday) : null;
  return (
    <div
      className={`fin-proj-detail-row${item.scenario ? ' is-scenario' : ''}${isTarget ? ' is-target' : ''}`}
      onClick={
        href
          ? (event) => openForecastTarget(href, event, navigate)
          : openCockpit
            ? () => onOpenCockpit?.()
            : undefined
      }
      onMouseEnter={peek && onPreviewHover ? () => onPreviewHover(peek) : undefined}
      onMouseLeave={peek && onPreviewHover ? (event) => keepPeekWhileInside(event, () => onPreviewHover(null)) : undefined}
    >
      <div className="fin-proj-detail-row-main">
        <span className="fin-proj-detail-project">
          {groupBadge && groupSectionKey && (
            <ForecastGroupBadge label={groupBadge} sectionKey={groupSectionKey} />
          )}
          {item.plannedKind === 'tax' ? item.label : item.projectLabel}
          {item.scenario && item.kind !== 'planned' && !item.payNowKey && (
            <span className="fin-proj-detail-scenario">Plan</span>
          )}
          {item.quotePlaceholder && (
            <span className="fin-proj-detail-job">Proposal · not finalized</span>
          )}
          {item.kind === 'job' && (
            <span className="fin-proj-detail-job">Job · estimated</span>
          )}
          {item.kind === 'planned' && (
            <span className="fin-proj-detail-scenario">{plannedKindLabel(item, canViewObligations)}</span>
          )}
        </span>
        <ForecastAmountTip
          amount={item.amount}
          out={out}
          bill={item.kind === 'ap' ? bill : undefined}
          slices={slices ?? []}
          peek={peek}
        />
      </div>
      <div className="fin-proj-detail-row-sub">
        <span>{item.plannedKind === 'tax' ? '' : item.label}</span>
        <span>
          {dueLabel(item)}
          {item.estimated && <span className="fin-proj-detail-est">est.</span>}
          {payNow && (
            <button
              type="button"
              className={`fin-pay-now-btn${payNow === 'on' ? ' is-on' : ''}`}
              aria-pressed={payNow === 'on'}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                onTogglePayNow?.(item);
              }}
            >
              {payNow === 'on' ? 'Paying today' : 'Pay now'}
            </button>
          )}
        </span>
      </div>
      <ForecastItemMeta
        item={item}
        bill={bill}
        hideProjectName={Boolean(groupBadge && groupBadge === item.projectName)}
      />
    </div>
  );
}

function ForecastProjectGroupCard({
  group,
  direction,
  expanded,
  onToggle,
  billsById,
  supplierTaskEndDates,
  billHasPreview,
  onPreviewHover,
  canViewObligations,
  payNowToday,
  onTogglePayNow,
}: {
  group: ForecastDetailGroup;
  direction: 'in' | 'out';
  expanded: boolean;
  onToggle: () => void;
  billsById: Map<string, SupplierBill>;
  supplierTaskEndDates: Map<string, Date>;
  billHasPreview: (bill: SupplierBill | undefined) => boolean;
  onPreviewHover: (peek: ForecastPeek | null) => void;
  canViewObligations: boolean;
  payNowToday?: Date;
  onTogglePayNow?: (item: CashFlowProjectionItem) => void;
}) {
  const navigate = useNavigate();
  const out = direction === 'out';
  const canExpand = group.items.length > 0 || group.quoteMarkers.length > 0;
  const href = forecastGroupHref(group);
  const peeks: ForecastPeek[] = [];
  for (const entry of group.items) {
    const entryBill = entry.billId ? billsById.get(entry.billId) : undefined;
    const entryPeek = forecastItemPeek(entry, { billHasFile: billHasPreview(entryBill) });
    if (entryPeek) peeks.push(entryPeek);
  }
  const groupPeek = peeks.length === 1 ? peeks[0] : null;
  return (
    <div className={`fin-proj-detail-group${expanded ? ' is-open' : ''}`}>
      <div
        className="fin-proj-detail-group-toggle"
        onMouseEnter={groupPeek ? () => onPreviewHover(groupPeek) : undefined}
        onMouseLeave={groupPeek ? (event) => keepPeekWhileInside(event, () => onPreviewHover(null)) : undefined}
      >
        {canExpand ? (
          <button
            type="button"
            className="fin-proj-detail-disclose"
            aria-expanded={expanded}
            aria-label={expanded ? `Collapse ${group.label}` : `Expand ${group.label}`}
            onClick={onToggle}
          >
            <ChevronRight size={14} className="fin-proj-detail-group-chevron" aria-hidden />
          </button>
        ) : (
          <span className="fin-proj-detail-bucket-chevron-slot" aria-hidden />
        )}
        <div
          className={`fin-proj-detail-jump${href ? ' is-target' : ''}`}
          onClick={(event) => {
            if (href) openForecastTarget(href, event, navigate);
            else if (canExpand) onToggle();
          }}
        >
          <span className="fin-proj-detail-group-name">{group.label}</span>
          {group.budgetOverBy ? (
            <HoverTip
              tip={(
                <span>
                  Approved POs are {formatCurrency(group.budgetOverBy)} over this month&apos;s budget.
                </span>
              )}
            >
              <span
                className="fin-proj-detail-budget-over"
                onClick={(event) => event.stopPropagation()}
              >
                Over
              </span>
            </HoverTip>
          ) : null}
          {group.quoteMarkers.length > 0 && (
            <AlertTriangle size={12} strokeWidth={2.4} className="fin-proj-detail-group-warn" aria-hidden />
          )}
          <span className={`fin-proj-detail-amt ${out ? 'fin-proj-detail-amt--out' : 'fin-proj-detail-amt--in'}`}>
            {out ? '−' : '+'}{formatCurrency(group.total)}
          </span>
        </div>
      </div>
      {canExpand && expanded && (
        <div className="fin-proj-detail-group-body">
          {group.quoteMarkers.map((marker) => (
            <div key={`qc-${marker.dealId}-${marker.kind}-${marker.taskId}-${marker.structureId || 'all'}`} className="fin-proj-detail-quote-coverage-row">
              <span className="fin-proj-detail-quote-coverage-text">
                {forecastQuoteCoverageMarkerLabel(marker)}
              </span>
              <Link
                className="fin-proj-detail-quote-coverage-link"
                to={forecastQuoteCoverageHubHref(marker.projectId)}
              >
                Open hub
              </Link>
            </div>
          ))}
          {group.items.map((item, idx) => {
            const bill = item.billId ? billsById.get(item.billId) : undefined;
            return (
              <ForecastDetailItemRow
                key={`${item.kind}-${item.billId || item.taskId || item.label}-${idx}`}
                item={item}
                direction={direction}
                bill={bill}
                slices={bill ? billScheduleSlices(bill, supplierTaskEndDates) : []}
                hasPreview={billHasPreview(bill)}
                onPreviewHover={onPreviewHover}
                canViewObligations={canViewObligations}
                payNowToday={payNowToday}
                onTogglePayNow={onTogglePayNow}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function ForecastMoneyOutBucketRow({
  bucket,
  sectionKey,
  expandedDetailGroups,
  onToggleGroup,
  billsById,
  supplierTaskEndDates,
  billHasPreview,
  onPreviewHover,
  canViewObligations,
  onOpenBurn,
  onOpenCockpit,
  payNowToday,
  onTogglePayNow,
}: {
  bucket: ForecastMoneyOutBucket;
  sectionKey: string;
  expandedDetailGroups: Set<string>;
  onToggleGroup: (key: string) => void;
  billsById: Map<string, SupplierBill>;
  supplierTaskEndDates: Map<string, Date>;
  billHasPreview: (bill: SupplierBill | undefined) => boolean;
  onPreviewHover: (peek: ForecastPeek | null) => void;
  canViewObligations: boolean;
  onOpenBurn: () => void;
  onOpenCockpit: () => void;
  payNowToday?: Date;
  onTogglePayNow?: (item: CashFlowProjectionItem) => void;
}) {
  const navigate = useNavigate();
  const toggleKey = `bucket:${sectionKey}:${bucket.key}`;
  const canExpand = bucket.entries.length > 0;
  const expanded = canExpand && expandedDetailGroups.has(toggleKey);
  const action = forecastBucketAction(bucket.key);
  const runAction = () => {
    if (action === 'purchasing') {
      navigate('/purchasing');
      return;
    }
    if (action === 'burn') onOpenBurn();
    else if (action === 'cockpit') onOpenCockpit();
    else if (!canExpand) return;
    else onToggleGroup(toggleKey);
  };
  const activate = (event: React.MouseEvent) => {
    if (action === 'purchasing') {
      openForecastTarget('/purchasing', event, navigate);
      return;
    }
    runAction();
  };
  return (
    <div className={`fin-proj-detail-bucket${expanded ? ' is-open' : ''}${canExpand ? '' : ' is-static'}`}>
      <div className={`fin-proj-detail-bucket-toggle${canExpand ? '' : ' is-static'}${action ? ' is-target' : ''}`}>
        {canExpand ? (
          <button
            type="button"
            className="fin-proj-detail-disclose"
            aria-expanded={expanded}
            aria-label={expanded ? `Collapse ${bucket.label}` : `Expand ${bucket.label}`}
            onClick={() => onToggleGroup(toggleKey)}
          >
            <ChevronRight size={14} className="fin-proj-detail-group-chevron" aria-hidden />
          </button>
        ) : (
          <span className="fin-proj-detail-bucket-chevron-slot" aria-hidden />
        )}
        <div className={`fin-proj-detail-jump${action ? ' is-target' : ''}`} onClick={activate}>
          <span className="fin-proj-detail-bucket-name">{bucket.label}</span>
          <span className="fin-proj-detail-amt fin-proj-detail-amt--out">
            −{formatCurrency(bucket.total)}
          </span>
        </div>
      </div>
      {bucket.sublabel && !expanded && (
        <div
          className={`fin-proj-detail-bucket-sub${action ? ' is-target' : ''}`}
          onClick={action ? activate : undefined}
        >
          {bucket.sublabel}
        </div>
      )}
      {expanded && (
        <div className="fin-proj-detail-bucket-body">
          {bucket.entries.map((entry, idx) => (
            <ForecastMoneyOutEntryRow
              key={`${toggleKey}-${entry.kind}-${idx}`}
              entry={entry}
              idx={idx}
              sectionKey={sectionKey}
              expandedDetailGroups={expandedDetailGroups}
              onToggleGroup={onToggleGroup}
              billsById={billsById}
              supplierTaskEndDates={supplierTaskEndDates}
              billHasPreview={billHasPreview}
              onPreviewHover={onPreviewHover}
              canViewObligations={canViewObligations}
              onOpenBurn={onOpenBurn}
              onOpenCockpit={onOpenCockpit}
              payNowToday={payNowToday}
              onTogglePayNow={onTogglePayNow}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ForecastMoneyOutEntryRow({
  entry,
  idx,
  sectionKey,
  expandedDetailGroups,
  onToggleGroup,
  billsById,
  supplierTaskEndDates,
  billHasPreview,
  onPreviewHover,
  canViewObligations,
  onOpenBurn,
  onOpenCockpit,
  payNowToday,
  onTogglePayNow,
}: {
  entry: ForecastMoneyOutEntry;
  idx: number;
  sectionKey: string;
  expandedDetailGroups: Set<string>;
  onToggleGroup: (key: string) => void;
  billsById: Map<string, SupplierBill>;
  supplierTaskEndDates: Map<string, Date>;
  billHasPreview: (bill: SupplierBill | undefined) => boolean;
  onPreviewHover: (peek: ForecastPeek | null) => void;
  canViewObligations: boolean;
  onOpenBurn: () => void;
  onOpenCockpit: () => void;
  payNowToday?: Date;
  onTogglePayNow?: (item: CashFlowProjectionItem) => void;
}) {
  if (entry.kind === 'bucket') {
    return (
      <ForecastMoneyOutBucketRow
        bucket={entry.bucket}
        sectionKey={sectionKey}
        expandedDetailGroups={expandedDetailGroups}
        onToggleGroup={onToggleGroup}
        billsById={billsById}
        supplierTaskEndDates={supplierTaskEndDates}
        billHasPreview={billHasPreview}
        onPreviewHover={onPreviewHover}
        canViewObligations={canViewObligations}
        onOpenBurn={onOpenBurn}
        onOpenCockpit={onOpenCockpit}
        payNowToday={payNowToday}
        onTogglePayNow={onTogglePayNow}
      />
    );
  }
  if (entry.kind === 'burn') {
    return (
      <div className="fin-proj-detail-row is-target" onClick={onOpenBurn}>
        <div className="fin-proj-detail-row-main">
          <span className="fin-proj-detail-project">{entry.label}</span>
          <span className="fin-proj-detail-amt fin-proj-detail-amt--out">
            −{formatCurrency(entry.amount)}
          </span>
        </div>
        {entry.sublabel && (
          <div className="fin-proj-detail-row-sub">
            <span>{entry.sublabel}</span>
          </div>
        )}
      </div>
    );
  }
  if (entry.kind === 'item') {
    const item = entry.item;
    const bill = item.billId ? billsById.get(item.billId) : undefined;
    return (
      <ForecastDetailItemRow
        key={`out-item-${item.billId || item.taskId || item.label}-${idx}`}
        item={item}
        direction="out"
        bill={bill}
        slices={bill ? billScheduleSlices(bill, supplierTaskEndDates) : []}
        hasPreview={billHasPreview(bill)}
        onPreviewHover={onPreviewHover}
        canViewObligations={canViewObligations}
        onOpenCockpit={onOpenCockpit}
        payNowToday={payNowToday}
        onTogglePayNow={onTogglePayNow}
      />
    );
  }
  const group = entry.group;
  return (
    <ForecastProjectGroupCard
      group={group}
      direction="out"
      expanded={expandedDetailGroups.has(`out:${group.key}`)}
      onToggle={() => onToggleGroup(`out:${group.key}`)}
      billsById={billsById}
      supplierTaskEndDates={supplierTaskEndDates}
      billHasPreview={billHasPreview}
      onPreviewHover={onPreviewHover}
      canViewObligations={canViewObligations}
      payNowToday={payNowToday}
      onTogglePayNow={onTogglePayNow}
    />
  );
}

function ForecastMoneyOutFlatList({
  sections,
  billsById,
  supplierTaskEndDates,
  billHasPreview,
  onPreviewHover,
  canViewObligations,
  onOpenBurn,
  onOpenCockpit,
  payNowToday,
  onTogglePayNow,
}: {
  sections: ForecastMoneyOutSection[];
  billsById: Map<string, SupplierBill>;
  supplierTaskEndDates: Map<string, Date>;
  billHasPreview: (bill: SupplierBill | undefined) => boolean;
  onPreviewHover: (peek: ForecastPeek | null) => void;
  canViewObligations: boolean;
  onOpenBurn: () => void;
  onOpenCockpit: () => void;
  payNowToday?: Date;
  onTogglePayNow?: (item: CashFlowProjectionItem) => void;
}) {
  const rows = flattenForecastMoneyOutSections(sections);
  if (rows.length === 0) return null;
  const summary = summarizeMoneyOutFlatRows(rows, (item) => (
    item.billId ? billsById.get(item.billId)?.category ?? null : null
  ));
  return (
    <div className="fin-proj-detail-flat">
      {summary.length > 1 && (
        <div className="fin-proj-detail-flat-summary">
          {summary.map((line) => (
            <div key={line.label} className="fin-proj-detail-flat-summary-row">
              <span>{line.label}</span>
              <span className="fin-proj-detail-amt fin-proj-detail-amt--out">
                −{formatCurrency(line.amount)}
              </span>
            </div>
          ))}
        </div>
      )}
      {rows.map((row, idx) => {
        const badge = flatRowGroupBadge(row);
        if (row.kind === 'item') {
          const bill = row.item.billId ? billsById.get(row.item.billId) : undefined;
          return (
            <ForecastDetailItemRow
              key={`flat-item-${row.item.billId || row.item.taskId || row.item.label}-${idx}`}
              item={row.item}
              direction="out"
              bill={bill}
              slices={bill ? billScheduleSlices(bill, supplierTaskEndDates) : []}
              hasPreview={billHasPreview(bill)}
              onPreviewHover={onPreviewHover}
              canViewObligations={canViewObligations}
              onOpenCockpit={onOpenCockpit}
              payNowToday={payNowToday}
              onTogglePayNow={onTogglePayNow}
              groupBadge={badge}
              groupSectionKey={row.sectionKey}
            />
          );
        }
        if (row.kind === 'quote') {
          return (
            <div key={`flat-quote-${row.marker.dealId}-${row.marker.kind}-${idx}`} className="fin-proj-detail-row">
              <div className="fin-proj-detail-quote-coverage-row">
                <span className="fin-proj-detail-quote-coverage-text">
                  {badge && <ForecastGroupBadge label={badge} sectionKey={row.sectionKey} />}
                  {forecastQuoteCoverageMarkerLabel(row.marker)}
                </span>
                <Link
                  className="fin-proj-detail-quote-coverage-link"
                  to={forecastQuoteCoverageHubHref(row.marker.projectId)}
                >
                  Open hub
                </Link>
              </div>
            </div>
          );
        }
        const openBurn = row.sectionKey === 'burn_pos_tax';
        return (
          <div
            key={`flat-${row.kind}-${idx}`}
            className={`fin-proj-detail-row${openBurn ? ' is-target' : ''}`}
            onClick={openBurn ? onOpenBurn : undefined}
          >
            <div className="fin-proj-detail-row-main">
              <span className="fin-proj-detail-project">
                {badge && <ForecastGroupBadge label={badge} sectionKey={row.sectionKey} />}
                {row.label}
              </span>
              <span className="fin-proj-detail-amt fin-proj-detail-amt--out">
                −{formatCurrency(row.amount)}
              </span>
            </div>
            {row.sublabel && (
              <div className="fin-proj-detail-row-sub">
                <span>{row.sublabel}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ForecastMoneyOutEntries({
  sections,
  flat,
  expandedDetailGroups,
  onToggleGroup,
  billsById,
  supplierTaskEndDates,
  billHasPreview,
  onPreviewHover,
  canViewObligations,
  onOpenBurn,
  onOpenCockpit,
  payNowToday,
  onTogglePayNow,
}: {
  sections: ForecastMoneyOutSection[];
  flat?: boolean;
  expandedDetailGroups: Set<string>;
  onToggleGroup: (key: string) => void;
  billsById: Map<string, SupplierBill>;
  supplierTaskEndDates: Map<string, Date>;
  billHasPreview: (bill: SupplierBill | undefined) => boolean;
  onPreviewHover: (peek: ForecastPeek | null) => void;
  canViewObligations: boolean;
  onOpenBurn: () => void;
  onOpenCockpit: () => void;
  payNowToday?: Date;
  onTogglePayNow?: (item: CashFlowProjectionItem) => void;
}) {
  if (flat) {
    return (
      <ForecastMoneyOutFlatList
        sections={sections}
        billsById={billsById}
        supplierTaskEndDates={supplierTaskEndDates}
        billHasPreview={billHasPreview}
        onPreviewHover={onPreviewHover}
        canViewObligations={canViewObligations}
        onOpenBurn={onOpenBurn}
        onOpenCockpit={onOpenCockpit}
        payNowToday={payNowToday}
        onTogglePayNow={onTogglePayNow}
      />
    );
  }
  return (
    <>
      {sections.map((section) => {
        const toggleKey = `section:${section.key}`;
        const expanded = expandedDetailGroups.has(toggleKey);
        return (
          <div
            key={section.key}
            className={`fin-proj-detail-section fin-proj-detail-section--${section.key}${expanded ? ' is-open' : ''}`}
          >
            <button
              type="button"
              className="fin-proj-detail-section-toggle"
              aria-expanded={expanded}
              onClick={() => onToggleGroup(toggleKey)}
            >
              <ChevronRight size={14} className="fin-proj-detail-group-chevron" aria-hidden />
              <span className="fin-proj-detail-section-title">{section.label}</span>
              <span className="fin-proj-detail-amt fin-proj-detail-amt--out">
                −{formatCurrency(section.total)}
              </span>
            </button>
            {expanded && (
              <div className="fin-proj-detail-section-body">
                {section.entries.map((entry, idx) => (
                  <ForecastMoneyOutEntryRow
                    key={`${section.key}-${entry.kind}-${idx}`}
                    entry={entry}
                    idx={idx}
                    sectionKey={section.key}
                    expandedDetailGroups={expandedDetailGroups}
                    onToggleGroup={onToggleGroup}
                    billsById={billsById}
                    supplierTaskEndDates={supplierTaskEndDates}
                    billHasPreview={billHasPreview}
                    onPreviewHover={onPreviewHover}
                    canViewObligations={canViewObligations}
                    onOpenBurn={onOpenBurn}
                    onOpenCockpit={onOpenCockpit}
                    payNowToday={payNowToday}
                    onTogglePayNow={onTogglePayNow}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

function ForecastHoverCashLine() {
  return (
    <Line
      dataKey={HOVER_CASH_KEY}
      stroke="transparent"
      strokeWidth={0}
      dot={false}
      activeDot={false}
      legendType="none"
      isAnimationActive={false}
      connectNulls={false}
    />
  );
}

const ForecastView: React.FC<ForecastViewProps> = ({
  overhead,
  positions,
  settings = DEFAULT_PAYROLL_SETTINGS,
  canWrite = true,
  canComment = true,
  budgetedOpexMonthly = 0,
  opexBudgetMonthlyByGroup,
}) => {
  const [horizon, setHorizon] = useState<Horizon>(12);

  // ── Cashflow history cache ────────────────────────────────
  const [cache, setCache] = useState<CashflowHistoryCache | null>(null);
  const [cacheLoaded, setCacheLoaded] = useState(false);
  const [method, setMethod] = useState<CashflowMethod>('cash');
  // Always show actuals when data exists (Cash/Accrual switch stays).
  const showActuals = true;
  const [flowFilter, setFlowFilter] = useState<ForecastFlowFilter>(FORECAST_FLOW_FILTER_OFF);
  const [moneyOutTags, setMoneyOutTags] = useState<MoneyOutFilterTag[]>([]);
  const [exportingCashflowKey, setExportingCashflowKey] = useState<string | null>(null);
  const [planningOpen, setPlanningOpen] = useState(false);
  const [cockpitOpen, setCockpitOpen] = useState(false);
  const [obligationYear, setObligationYear] = useState(() => new Date().getFullYear());
  const [bonusDraft, setBonusDraft] = useState<BonusPlan | null>(null);
  const [taxDraft, setTaxDraft] = useState<TaxPlan | null>(null);
  const [settingsDraft, setSettingsDraft] = useState<ObligationSettings | null>(null);
  const [scenarioLines, setScenarioLines] = useState<ScenarioLine[]>([]);
  const [earlyPayKeys, setEarlyPayKeys] = useState<Set<string>>(() => new Set());
  const [stackedPlanIds, setStackedPlanIds] = useState<string[]>([]);
  const [planTitle, setPlanTitle] = useState('');
  const [planWhy, setPlanWhy] = useState('');
  const loadedPlanIdRef = useRef<string | null>(null);
  const lastSavedPlanFpRef = useRef('');
  const planDraftReadyRef = useRef(true);
  const persistInFlightRef = useRef<Promise<string | null> | null>(null);
  const [planSaveState, setPlanSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [searchParams, setSearchParams] = useSearchParams();
  const [benefitsSettings, setBenefitsSettings] = useState(defaultEmployeeBenefitsSettings);
  const [billPayClearing, setBillPayClearing] = useState(DEFAULT_BILL_PAY_CLEARING);

  useEffect(() => {
    const unsub = subscribeEmployeeBenefitsSettings(
      setBenefitsSettings,
      () => undefined,
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = subscribeBillPayClearingSettings(setBillPayClearing);
    return unsub;
  }, []);

  const payrollSchedule = useMemo(
    () => resolveForecastPayrollSchedule(benefitsSettings.payrollSchedule),
    [benefitsSettings.payrollSchedule],
  );

  const forecastToday = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);
  const planParam = searchParams.get('plan');

  const { user } = useAuth();
  const canViewObligations = canViewBonusAndTaxPlans(user?.role);
  const {
    rfqSubmissions,
    crmUsers,
    enhancedDeals = [],
    timberProjects = [],
    rfqProjects = [],
    rfqPackages = [],
    projectTasks = [],
    taskGroups = [],
    projectOnboardings = [],
  } = useCRM();
  useCRMCollections(['projectTasks', 'taskGroups', 'projectOnboardings']);

  const scheduleTasks = useMemo(
    () => [...projectTasks, ...mirrorAllOnboardingTasks(projectOnboardings, taskGroups)],
    [projectTasks, projectOnboardings, taskGroups],
  );
  const designations = useMemo(
    () => resolveEmployeeDesignations(user, crmUsers),
    [crmUsers, user],
  );
  const briefingProfile = useMemo(() => {
    const fromAuth = user?.executiveBriefing;
    const fromCrm = crmUsers.find((u) => u.uid === user?.id)?.executiveBriefing;
    const cfg = fromAuth?.enabled ? fromAuth : fromCrm?.enabled ? fromCrm : undefined;
    if (!cfg?.enabled) return undefined;
    if (!cfg.profile || cfg.profile === 'coo') return 'coo' as const;
    return cfg.profile;
  }, [crmUsers, user]);

  const workspace = useCapitalPlanWorkspace({
    actorUserId: user?.id,
    actorName: user?.name || user?.email || 'Someone',
    authRole: user?.role,
    designations,
    briefingProfile,
    crmUsers,
    initialPlanId: planParam,
  });

  const obligationYears = useMemo(() => obligationYearsForHorizon(horizon), [horizon]);
  const obligations = useObligationWorkspace({
    years: obligationYears,
    positions,
    payrollSettings: settings,
    crmUsers,
    actorUserId: user?.id,
  });

  useEffect(() => {
    if (!obligationYears.includes(obligationYear)) {
      setObligationYear(obligationYears[0] ?? new Date().getFullYear());
    }
  }, [obligationYear, obligationYears]);

  const prevActivePlanIdRef = useRef<string | null>(workspace.activePlanId);

  useEffect(() => {
    if (planParam) setPlanningOpen(true);
  }, [planParam]);

  useEffect(() => {
    const id = workspace.activePlanId;
    setStackedPlanIds((prev) => selectCapitalPlanOverlay({
      selectedId: id,
      previousSelectedId: prevActivePlanIdRef.current,
      previousStackedIds: prev,
    }));
    prevActivePlanIdRef.current = id;
  }, [workspace.activePlanId]);

  const planFingerprint = useCallback((title: string, why: string, lines: ScenarioLine[]) => (
    JSON.stringify({
      title: title.trim(),
      why,
      lines: serializeScenarioLines(lines),
    })
  ), []);

  const planDraftRef = useRef({ title: planTitle, why: planWhy, lines: scenarioLines });
  planDraftRef.current = { title: planTitle, why: planWhy, lines: scenarioLines };

  const applyLoadedPlan = useCallback((plan: {
    id: string;
    title: string;
    why: string;
    lines: ScenarioLine[];
  }) => {
    planDraftReadyRef.current = false;
    loadedPlanIdRef.current = plan.id;
    lastSavedPlanFpRef.current = planFingerprint(plan.title, plan.why, plan.lines);
    setScenarioLines(plan.lines);
    setPlanTitle(plan.title);
    setPlanWhy(plan.why);
    setPlanSaveState('saved');
  }, [planFingerprint]);

  useEffect(() => {
    const plan = workspace.activePlan;
    if (!plan) return;
    if (loadedPlanIdRef.current === plan.id) return;
    applyLoadedPlan(plan);
  }, [applyLoadedPlan, workspace.activePlan]);

  useEffect(() => {
    if (planDraftReadyRef.current) return;
    if (loadedPlanIdRef.current !== (workspace.activePlanId ?? null)) return;
    const fp = planFingerprint(planTitle, planWhy, scenarioLines);
    if (fp !== lastSavedPlanFpRef.current) return;
    planDraftReadyRef.current = true;
  }, [planFingerprint, planTitle, planWhy, scenarioLines, workspace.activePlanId]);

  const persistPlan = useCallback(async (opts?: { share?: boolean; quiet?: boolean }): Promise<string | null> => {
    if (!canWrite) return workspace.activePlanId;
    const share = opts?.share === true;
    const quiet = !share && opts?.quiet !== false;
    const run = async (): Promise<string | null> => {
      const draft = planDraftRef.current;
      const fp = planFingerprint(draft.title, draft.why, draft.lines);
      if (!share && fp === lastSavedPlanFpRef.current) return workspace.activePlanId;
      if (!shouldAutosaveCapitalPlan({
        activePlanId: workspace.activePlanId,
        hydratedPlanId: loadedPlanIdRef.current,
        draftReady: planDraftReadyRef.current,
        title: draft.title,
        why: draft.why,
        lines: draft.lines,
      })) {
        return workspace.activePlanId;
      }
      if (quiet) setPlanSaveState('saving');
      const id = await workspace.save({
        title: draft.title,
        why: draft.why,
        lines: draft.lines,
        share,
        quiet,
      });
      if (!id) {
        if (quiet) setPlanSaveState('idle');
        return null;
      }
      loadedPlanIdRef.current = id;
      lastSavedPlanFpRef.current = fp;
      setSearchParams({ plan: id }, { replace: true });
      if (quiet) setPlanSaveState('saved');
      return id;
    };

    const queued = (persistInFlightRef.current ?? Promise.resolve(null)).then(run, run);
    persistInFlightRef.current = queued;
    const id = await queued;
    if (persistInFlightRef.current === queued) persistInFlightRef.current = null;
    if (id) {
      const latest = planFingerprint(
        planDraftRef.current.title,
        planDraftRef.current.why,
        planDraftRef.current.lines,
      );
      if (!share && latest !== lastSavedPlanFpRef.current) {
        return persistPlan({ quiet: true });
      }
    }
    return id;
  }, [canWrite, planFingerprint, setSearchParams, workspace]);

  const persistPlanAs = useCallback(
    (): Promise<string | null> => persistPlan({ quiet: true }),
    [persistPlan],
  );

  const openSavedPlan = useCallback((planId: string) => {
    void persistPlan({ quiet: true }).then(() => {
      const plan = workspace.plans.find((row) => row.id === planId);
      if (plan) applyLoadedPlan(plan);
      workspace.setActivePlanId(planId);
      setSearchParams({ plan: planId }, { replace: true });
      setPlanningOpen(true);
    });
  }, [applyLoadedPlan, persistPlan, setSearchParams, workspace]);

  const startUnsavedPlan = useCallback(() => {
    void persistPlan({ quiet: true }).then(() => {
      workspace.startNew();
      loadedPlanIdRef.current = null;
      planDraftReadyRef.current = true;
      lastSavedPlanFpRef.current = planFingerprint('', '', []);
      setScenarioLines([]);
      setPlanTitle('');
      setPlanWhy('');
      setPlanSaveState('idle');
      setSearchParams({}, { replace: true });
    });
  }, [persistPlan, planFingerprint, setSearchParams, workspace]);

  const deleteSavedPlan = useCallback((planId: string) => {
    void (async () => {
      const deletingActive = workspace.activePlanId === planId;
      if (deletingActive) {
        workspace.startNew();
        loadedPlanIdRef.current = null;
        planDraftReadyRef.current = true;
        lastSavedPlanFpRef.current = planFingerprint('', '', []);
        setScenarioLines([]);
        setPlanTitle('');
        setPlanWhy('');
        setPlanSaveState('idle');
        setSearchParams({}, { replace: true });
      }
      const ok = await workspace.remove(planId);
      if (ok) {
        setStackedPlanIds((prev) => prev.filter((id) => id !== planId));
      }
    })();
  }, [planFingerprint, setSearchParams, workspace]);

  useEffect(() => {
    if (!planningOpen || !canWrite) return;
    if (!shouldAutosaveCapitalPlan({
      activePlanId: workspace.activePlanId,
      hydratedPlanId: loadedPlanIdRef.current,
      draftReady: planDraftReadyRef.current,
      title: planTitle,
      why: planWhy,
      lines: scenarioLines,
    })) {
      return;
    }
    const fp = planFingerprint(planTitle, planWhy, scenarioLines);
    if (fp === lastSavedPlanFpRef.current) return;
    const timer = window.setTimeout(() => {
      void persistPlan({ quiet: true });
    }, 450);
    return () => window.clearTimeout(timer);
  }, [canWrite, planFingerprint, planTitle, planWhy, planningOpen, persistPlan, scenarioLines, workspace.activePlanId]);

  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [refreshNotice, setRefreshNotice] = useState<string | null>(null);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const [dailyReport, setDailyReport] = useState<DailyFinanceReport | null>(null);
  const [quickLook, setQuickLook] = useState<
    | { kind: 'file'; url: string; name: string }
    | { kind: 'summary'; name: string; lines: string[] }
    | null
  >(null);
  const [quickLookLoading, setQuickLookLoading] = useState(false);
  const quickLookOpenRef = useRef(false);
  const previewHoverRef = useRef<ForecastPeek | null>(null);

  useEffect(() => {
    const ref = doc(db, 'financeSettings', 'cashflowHistoryCache');
    const unsub = onSnapshot(
      ref,
      snap => {
        setSnapshotError(null);
        if (!snap.exists()) {
          setCache(null);
        } else {
          const data: any = snap.data();
          setCache({
            cash: (data.cash as CashflowMethodData | undefined) ?? null,
            accrual: (data.accrual as CashflowMethodData | undefined) ?? null,
            rangeStart: data.rangeStart ?? null,
            rangeEnd: data.rangeEnd ?? null,
            lastSyncedAt: data.lastSyncedAt?.toDate?.() ?? null,
          });
        }
        setCacheLoaded(true);
      },
      err => {
        console.warn('[ForecastView] cashflow snapshot error', err);
        setSnapshotError('Unable to read cached QuickBooks history. Confirm you are signed in as an admin.');
        setCacheLoaded(true);
      },
    );
    return unsub;
  }, []);

  useEffect(() => {
    const ref = doc(db, 'financeSettings', 'dailyFinanceReport');
    const unsub = onSnapshot(
      ref,
      snap => {
        if (!snap.exists()) {
          setDailyReport(null);
          return;
        }
        const data = snap.data();
        setDailyReport({
          sequencePodAmount: typeof data.sequencePodAmount === 'number' ? data.sequencePodAmount : 0,
          dateKey: typeof data.dateKey === 'string' ? data.dateKey : null,
          sequencePods: data.sequencePods,
          history: data.history,
          source: data.source,
          updatedAt: data.updatedAt?.toDate?.() ?? null,
        });
      },
      err => console.warn('[ForecastView] daily finance report snapshot error', err),
    );
    return unsub;
  }, []);

  const activeMethodData = useMemo<CashflowMethodData | null>(() => {
    if (!cache) return null;
    return method === 'cash' ? cache.cash : cache.accrual;
  }, [cache, method]);

  const hasActualsData = !!activeMethodData?.months?.length;

  const cashBalanceByMonthKey = useMemo(
    () => buildCashBalanceByMonthKey(dailyReport),
    [dailyReport],
  );

  const sequenceByDay = useMemo(
    () => sequenceCashByDayKey(dailyReport),
    [dailyReport],
  );

  const hasCashBalanceData = Object.keys(cashBalanceByMonthKey).length > 0;

  const [podSyncing, setPodSyncing] = useState(false);
  const syncingSources = refreshing || podSyncing;

  const onRefreshAll = useCallback(async () => {
    setRefreshing(true);
    setPodSyncing(true);
    setRefreshError(null);
    setRefreshNotice(null);
    const notes: string[] = [];
    const errors: string[] = [];

    const historyP = refreshCashflowHistory(36)
      .then((summary) => {
        if (summary.monthCount > 0) {
          notes.push(
            `QuickBooks: ${summary.monthCount} months (${summary.accountCount} accounts) · ${summary.rangeStart} → ${summary.rangeEnd}`,
          );
        } else {
          errors.push('QuickBooks returned no monthly data. Check your P&L date range in QuickBooks.');
        }
      })
      .catch((err: any) => {
        errors.push(err?.message || 'Unable to refresh QuickBooks history');
      })
      .finally(() => setRefreshing(false));

    // Lazy import — keeps firebase/functions out of the initial chunk (and jsdom tests).
    const podP = import('../../utils/sequenceApi')
      .then((m) => m.syncDailyFinanceReport())
      .then((snap) => {
        notes.push(
          `Sequence: ${formatCurrency(snap.sequencePodAmount)} across ${snap.podCount} accounts (${snap.dateKey})`,
        );
      })
      .catch((err: any) => {
        errors.push(err?.message || 'Unable to sync pod balance');
      })
      .finally(() => setPodSyncing(false));

    await Promise.all([historyP, podP]);
    if (notes.length) setRefreshNotice(notes.join(' · '));
    if (errors.length) setRefreshError(errors.join(' · '));
  }, []);

  // ── Forecast computation ──────────────────────────────────
  const buckets = useMemo(() => {
    const fromMonth = new Date();
    fromMonth.setDate(1);
    return forecastMonthly(overhead, positions, settings, {
      fromMonth,
      monthsAhead: horizon,
      budgetedOpexMonthly,
    });
  }, [overhead, positions, settings, horizon, budgetedOpexMonthly]);

  const timeline = useMemo(
    () => buildForecastChartTimeline(horizon),
    [horizon],
  );

  // Past months stay on the axis whenever pod cash exists, so the historic
  // cash line is still drawn with Show actuals off. The toggle only adds
  // QuickBooks revenue, COGS, marketing, and overhead.
  const showHistoryWindow = hasCashBalanceData || (showActuals && hasActualsData);

  const axisMonths = useMemo(() => {
    if (showHistoryWindow) {
      return timeline.map((t) => ({
        monthKey: t.monthKey,
        month: t.month,
        label: t.label,
      }));
    }
    return buckets.map((b) => ({
      monthKey: toMonthKey(b.month),
      month: b.month,
      label: b.label,
    }));
  }, [showHistoryWindow, timeline, buckets]);

  const nMonths = axisMonths.length;
  const monthStarts = useMemo(() => axisMonths.map((m) => m.month), [axisMonths]);

  const overlayPoints = useMemo<CashflowOverlayPoint[]>(() => {
    if (!showActuals || !activeMethodData) return [];
    return buildOverlayPoints(activeMethodData, new Date());
  }, [showActuals, activeMethodData]);

  const overlayByMonthKey = useMemo(() => {
    return new Map(overlayPoints.map(p => [p.monthKey, p]));
  }, [overlayPoints]);

  const overlayByLabel = useMemo(() => {
    return new Map(overlayPoints.map(p => [p.label, p]));
  }, [overlayPoints]);

  // ── Projected cash balance (AR − AP − burn) ───────────────
  const {
    moneyInRows,
    salesOrders = [],
    changeOrders = [],
    supplierBills,
    supplierTaskEndDates,
    taskNameById,
    projectNameById,
    projectIdByDealId,
    taskProjectIdById,
    jobCostItems = [],
    issuedPurchaseAp = [],
    contractorInvoiceAp = [],
    proposalsByDeal = {},
    loaded: billingLoaded,
    arError: projArError,
    apError: projApError,
  } = useCashFlowProjectionData(true);

  const billsById = useMemo(() => {
    const m = new Map<string, SupplierBill>();
    for (const bill of supplierBills) {
      if (bill.id) m.set(bill.id, bill);
    }
    return m;
  }, [supplierBills]);

  const submissionsById = useMemo(() => {
    const m = new Map<string, RfqSubmission>();
    for (const s of rfqSubmissions) if (s.id) m.set(s.id, s);
    return m;
  }, [rfqSubmissions]);

  const submissionsByProjectOrg = useMemo(() => {
    const m = new Map<string, RfqSubmission[]>();
    for (const s of rfqSubmissions) {
      if (!s.storagePath || !s.rfqProjectId || !s.supplierOrgId) continue;
      const key = `${s.rfqProjectId}:${s.supplierOrgId}`;
      const list = m.get(key) ?? [];
      list.push(s);
      m.set(key, list);
    }
    m.forEach((list) => {
      list.sort((a, b) => (b.createdAt?.getTime?.() ?? 0) - (a.createdAt?.getTime?.() ?? 0));
    });
    return m;
  }, [rfqSubmissions]);

  const findPreviewSubmission = useCallback(
    (bill: SupplierBill): RfqSubmission | null => {
      const subId = bill.rfqSubmissionId;
      if (subId) {
        const sub = submissionsById.get(subId);
        if (sub?.storagePath) return sub;
      }
      if (bill.rfqProjectId && bill.supplierOrgId) {
        const list = submissionsByProjectOrg.get(`${bill.rfqProjectId}:${bill.supplierOrgId}`);
        if (list?.[0]) return list[0];
      }
      return null;
    },
    [submissionsById, submissionsByProjectOrg],
  );

  const rfqProjectsById = useMemo(() => {
    const m = new Map<string, RfqProject>();
    for (const p of rfqProjects) if (p.id) m.set(p.id, p);
    return m;
  }, [rfqProjects]);

  // Structures a timber/CNC bill pays for. Resolved from the approved quote
  // behind the bill — a structure-split quote lists them directly, while a
  // full-scope quote inherits the structures the supplier was asked to price
  // (package scope / invite). Whole-job awards return nothing to badge.
  const structuresForBill = useCallback(
    (bill: SupplierBill): string[] => {
      if (!bill.supplierOrgId) return [];
      const project = bill.rfqProjectId ? rfqProjectsById.get(bill.rfqProjectId) : undefined;
      if (project) {
        return approvedStructuresForSupplierOnProject({
          project,
          supplierOrgId: bill.supplierOrgId,
          rfqPackages,
          rfqSubmissions,
          submissionId: bill.rfqSubmissionId,
        }).map((s) => s.name || s.id);
      }
      // No RFQ project loaded — fall back to whatever the submission itself lists.
      const direct = bill.rfqSubmissionId ? submissionsById.get(bill.rfqSubmissionId) : undefined;
      if (direct?.quotedStructures?.length) {
        return normalizeQuotedStructures(direct.quotedStructures).map((s) => s.name || s.id);
      }
      if (!bill.rfqProjectId) return [];
      const refs = rfqSubmissions
        .filter((s) => s.rfqProjectId === bill.rfqProjectId && s.supplierOrgId === bill.supplierOrgId)
        .flatMap((s) => s.quotedStructures ?? []);
      return normalizeQuotedStructures(refs).map((s) => s.name || s.id);
    },
    [rfqProjectsById, rfqPackages, rfqSubmissions, submissionsById],
  );
  const billMetaCtx = useMemo(() => ({ structuresForBill }), [structuresForBill]);

  const billHasPreview = useCallback(
    (bill: SupplierBill | undefined): boolean => {
      if (!bill) return false;
      if (bill.fileUrl || bill.storagePath) return true;
      return !!findPreviewSubmission(bill);
    },
    [findPreviewSubmission],
  );

  const openPeek = useCallback(
    async (peek: ForecastPeek) => {
      if (peek.kind === 'summary') {
        setQuickLook({ kind: 'summary', name: peek.title, lines: peek.lines });
        return;
      }
      setQuickLookLoading(true);
      try {
        if (peek.kind === 'file') {
          let url = peek.url ?? null;
          if (!url && peek.storagePath) {
            url = await getDownloadURL(storageRef(storage, peek.storagePath));
          }
          if (url) setQuickLook({ kind: 'file', url, name: peek.name });
          return;
        }
        const bill = billsById.get(peek.billId);
        if (!bill) return;
        let url: string | null = bill.fileUrl ?? null;
        let name = bill.fileName || bill.description || bill.vendorName;
        if (!url && bill.storagePath) {
          url = await getDownloadURL(storageRef(storage, bill.storagePath));
        }
        if (!url) {
          const sub = findPreviewSubmission(bill);
          if (sub?.storagePath) {
            url = await getDownloadURL(storageRef(storage, sub.storagePath));
            name = sub.fileName || name;
          }
        }
        if (url) setQuickLook({ kind: 'file', url, name });
      } catch {
        // Missing/inaccessible file — quietly do nothing, same as Finder.
      } finally {
        setQuickLookLoading(false);
      }
    },
    [billsById, findPreviewSubmission],
  );

  useEffect(() => {
    quickLookOpenRef.current = !!quickLook || quickLookLoading;
  }, [quickLook, quickLookLoading]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && quickLookOpenRef.current) {
        setQuickLook(null);
        return;
      }
      if (e.code !== 'Space') return;
      if (eventTargetIsEditable(e)) return;
      if (quickLookOpenRef.current) {
        e.preventDefault();
        setQuickLook(null);
        return;
      }
      const peek = previewHoverRef.current;
      if (!peek) return;
      e.preventDefault();
      void openPeek(peek);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openPeek]);

  // Only the latest pod balance is required — the dashed line anchors at the
  // current month even when there is no monthly balance history yet.
  const obligationDrafting = cockpitOpen && canViewObligations;
  const plannedItems = useMemo(
    () => obligations.slicesFor({
      bonusDraft: obligationDrafting ? bonusDraft : null,
      taxDraft: obligationDrafting ? taxDraft : null,
    }),
    [bonusDraft, obligationDrafting, obligations, taxDraft],
  );

  const savedPlannedItems = obligations.slices;

  const horizonMonthKeys = useMemo(
    () => buckets.map((b) => toMonthKey(b.month)),
    [buckets],
  );

  const quoteCoverageMarkers = useMemo(
    () => buildForecastQuoteCoverageMarkers({
      moneyInRows,
      horizonMonthKeys,
      supplierBills,
      timberProjects,
      enhancedDeals,
      rfqProjects,
      rfqPackages,
      rfqSubmissions,
      projectTasks: scheduleTasks,
      taskGroups,
      projectIdByDealId,
      projectNameById,
      proposalsByDeal,
      changeOrders,
    }),
    [
      moneyInRows,
      horizonMonthKeys,
      supplierBills,
      timberProjects,
      enhancedDeals,
      rfqProjects,
      rfqPackages,
      rfqSubmissions,
      scheduleTasks,
      taskGroups,
      projectIdByDealId,
      projectNameById,
      proposalsByDeal,
      changeOrders,
    ],
  );

  const quoteCoverageByMonthKey = useMemo(
    () => groupForecastQuoteCoverageMarkersByMonth(quoteCoverageMarkers),
    [quoteCoverageMarkers],
  );

  const quotePlaceholderAp = useMemo(
    () => quotePlaceholderApSlicesFromMarkers(quoteCoverageMarkers),
    [quoteCoverageMarkers],
  );

  const awardedQuoteGaps = useMemo(
    () => buildAwardedQuoteApFromPage({ rfqProjects, rfqSubmissions, supplierBills }),
    [rfqProjects, rfqSubmissions, supplierBills],
  );
  const awardedQuoteAp = awardedQuoteGaps.slices;
  const omitSupplierBillIds = awardedQuoteGaps.omitBillIds;

  const projection = useMemo(() => {
    if (!billingLoaded || !dailyReport) return null;
    return buildCashFlowProjection(assembleCashFlowProjectionInputs({
      startingBalance: dailyReport.sequencePodAmount,
      moneyInRows,
      supplierBills,
      supplierTaskEndDates,
      taskNameById,
      projectNameById,
      projectIdByDealId,
      taskProjectIdById,
      burnBuckets: buckets,
      plannedItems,
      jobCostItems,
      quotePlaceholderAp,
      issuedPurchaseAp,
      awardedQuoteAp,
      omitSupplierBillIds,
      contractorInvoiceAp,
      opexBudgetMonthlyByGroup,
      payrollSchedule,
      billPayClearing,
      monthsAhead: horizon,
      now: forecastToday,
    }));
  }, [billingLoaded, dailyReport, moneyInRows, supplierBills, supplierTaskEndDates, taskNameById, projectNameById, projectIdByDealId, taskProjectIdById, jobCostItems, quotePlaceholderAp, issuedPurchaseAp, awardedQuoteAp, omitSupplierBillIds, contractorInvoiceAp, opexBudgetMonthlyByGroup, buckets, plannedItems, horizon, payrollSchedule, billPayClearing, forecastToday]);

  const savedProjection = useMemo(() => {
    if (!billingLoaded || !dailyReport) return null;
    if (!obligationDrafting) return projection;
    return buildCashFlowProjection(assembleCashFlowProjectionInputs({
      startingBalance: dailyReport.sequencePodAmount,
      moneyInRows,
      supplierBills,
      supplierTaskEndDates,
      taskNameById,
      projectNameById,
      projectIdByDealId,
      taskProjectIdById,
      burnBuckets: buckets,
      plannedItems: savedPlannedItems,
      jobCostItems,
      quotePlaceholderAp,
      issuedPurchaseAp,
      awardedQuoteAp,
      omitSupplierBillIds,
      contractorInvoiceAp,
      opexBudgetMonthlyByGroup,
      payrollSchedule,
      billPayClearing,
      monthsAhead: horizon,
      now: forecastToday,
    }));
  }, [billingLoaded, dailyReport, horizon, moneyInRows, obligationDrafting, plannedItems, projection, savedPlannedItems, supplierBills, supplierTaskEndDates, taskNameById, projectNameById, projectIdByDealId, taskProjectIdById, jobCostItems, quotePlaceholderAp, issuedPurchaseAp, awardedQuoteAp, omitSupplierBillIds, contractorInvoiceAp, opexBudgetMonthlyByGroup, buckets, payrollSchedule, billPayClearing, forecastToday]);

  /** Why the projection is not drawn — surfaced instead of failing silently. */
  const projectionUnavailableReason = useMemo(() => {
    if (!dailyReport) {
      return 'Projected cash needs the daily Sequence pod balance snapshot — none found yet.';
    }
    if (!billingLoaded) return 'Loading billing data for the cash projection…';
    return null;
  }, [dailyReport, billingLoaded]);

  const projectionByMonthKey = useMemo(() => {
    const m = new Map<string, NonNullable<typeof projection>['months'][number]>();
    for (const p of projection?.months ?? []) m.set(p.monthKey, p);
    return m;
  }, [projection]);

  const bakedLines = useMemo(
    () => bakedApprovedCapexLines(workspace.plans, {
      id: workspace.activePlanId,
      status: workspace.activePlan?.status,
      lines: scenarioLines,
      useLocalLines: planningOpen && loadedPlanIdRef.current === workspace.activePlanId,
    }),
    [planningOpen, scenarioLines, workspace.activePlan?.status, workspace.activePlanId, workspace.plans],
  );
  const coveredBillIds = useMemo(() => coveredCapexBillIds(supplierBills), [supplierBills]);

  const bakedApplied = useMemo(
    () => (projection
      ? applyCashFlowScenario(projection, bakedLines, {
        coveredBillIds,
        bills: supplierBills,
        taskEndDateById: supplierTaskEndDates,
      })
      : null),
    [bakedLines, coveredBillIds, projection, supplierBills, supplierTaskEndDates],
  );
  const bakedProjection = bakedApplied?.projection ?? projection;
  const bakedByMonthKey = useMemo(() => {
    const m = new Map<string, NonNullable<typeof bakedProjection>['months'][number]>();
    for (const p of bakedProjection?.months ?? []) m.set(p.monthKey, p);
    return m;
  }, [bakedProjection]);

  const overlayLines = useMemo(
    () => stackedCapexOverlayLines({
      stackedPlanIds,
      plans: workspace.plans,
      activePlanId: workspace.activePlanId,
      activeLines: scenarioLines,
    }),
    [scenarioLines, stackedPlanIds, workspace.activePlanId, workspace.plans],
  );

  const earlyPayLines = useMemo(
    () => payNowShiftLines(bakedProjection, earlyPayKeys, forecastToday),
    [bakedProjection, earlyPayKeys, forecastToday],
  );

  const stackedPlans = useMemo(
    () => stackedPlanIds
      .map((id) => workspace.plans.find((p) => p.id === id))
      .filter((p): p is NonNullable<typeof p> => !!p && p.status !== 'approved' && p.status !== 'completed'),
    [stackedPlanIds, workspace.plans],
  );

  const scenarioApplied = useMemo(
    () => (bakedProjection
      ? applyCashFlowScenario(bakedProjection, [...overlayLines, ...earlyPayLines], {
        coveredBillIds,
        bills: supplierBills,
        taskEndDateById: supplierTaskEndDates,
      })
      : null),
    [bakedProjection, coveredBillIds, earlyPayLines, overlayLines, supplierBills, supplierTaskEndDates],
  );
  const scenarioActive = !!scenarioApplied?.hasEffect;
  const enabledLineCount = scenarioLines.filter((l) => l.enabled).length;
  const payNowOnly = earlyPayLines.length > 0 && !scenarioHasEffect(overlayLines);
  const overlayChartName = payNowOnly
    ? 'Paying now'
    : stackedPlans.length > 1
      ? `CapEx · ${stackedPlans.length} plans`
      : 'With CapEx';

  const togglePayNow = useCallback((item: CashFlowProjectionItem) => {
    const key = item.payNowKey || projectionItemKey(item);
    setEarlyPayKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleStackedPlan = useCallback((planId: string) => {
    setStackedPlanIds((prev) => toggleCapitalPlanComparePin({
      planId,
      selectedId: workspace.activePlanId,
      stackedIds: prev,
    }));
  }, [workspace.activePlanId]);
  const displayProjection = scenarioApplied?.hasEffect
    ? scenarioApplied.projection
    : (bakedProjection ?? projection);
  const flowFilterProjects = useMemo(
    () => listForecastFlowFilterProjects(
      (displayProjection?.months ?? []).flatMap((month) => month.items ?? []),
      quoteCoverageMarkers,
    ),
    [displayProjection, quoteCoverageMarkers],
  );
  const exportProjectCashflow = useCallback((job: ForecastFlowFilterProject) => {
    if (!displayProjection || exportingCashflowKey) return;
    setExportingCashflowKey(job.key);
    void import('../../utils/projectCashflowReport')
      .then(({ exportProjectCashflowPdf }) => exportProjectCashflowPdf({
        projectKey: job.key,
        projectLabel: job.label,
        months: displayProjection.months,
        horizonMonths: horizon,
      }))
      .catch((err) => {
        console.error('[ForecastView] project cashflow pdf', err);
      })
      .finally(() => setExportingCashflowKey(null));
  }, [displayProjection, exportingCashflowKey, horizon]);
  const displayByMonthKey = useMemo(() => {
    const m = new Map<string, NonNullable<typeof displayProjection>['months'][number]>();
    for (const p of displayProjection?.months ?? []) m.set(p.monthKey, p);
    return m;
  }, [displayProjection]);
  const scenarioByMonthKey = useMemo(() => {
    const m = new Map<string, NonNullable<typeof scenarioApplied>['projection']['months'][number]>();
    for (const p of scenarioApplied?.projection.months ?? []) m.set(p.monthKey, p);
    return m;
  }, [scenarioApplied]);

  const currentMonthKey = useMemo(
    () => (buckets[0] ? toMonthKey(buckets[0].month) : toMonthKey(new Date())),
    [buckets],
  );


  const profitHint = useMemo(() => {
    const methodData = cache?.[method] ?? null;
    return hintAnnualProfitFromCashflow(methodData?.months, methodData?.totals);
  }, [cache, method]);

  const bonusPlan = bonusDraft ?? obligations.bonusFor(obligationYear);
  const taxPlan = taxDraft ?? obligations.taxFor(obligationYear);
  const bonusComputed = obligations.computedBonus(
    obligationYear,
    obligationDrafting ? bonusDraft ?? undefined : undefined,
    obligationDrafting ? taxDraft ?? undefined : undefined,
  );
  const taxComputed = obligations.computedTax(
    obligationYear,
    obligationDrafting ? taxDraft ?? undefined : undefined,
    obligationDrafting ? bonusDraft ?? undefined : undefined,
  );

  // ── Build a unified [past, future] chart-data array ───────
  const monthlyChartData = useMemo(() => {
    const fillActuals = (row: ForecastChartRow, p: CashflowOverlayPoint) => {
      row[ACTUAL_REVENUE_KEY] = Math.round(p.actualRevenue);
      row[ACTUAL_COGS_KEY] = Math.round(p.actualCogs);
      row[ACTUAL_MARKETING_KEY] = Math.round(p.actualMarketing);
      row[ACTUAL_OVERHEAD_KEY] = Math.round(p.actualOverhead);
      row.expenseBreakdown = p.expenseBreakdown;
    };

    const clearActuals = (row: ForecastChartRow) => {
      row[ACTUAL_REVENUE_KEY] = null;
      row[ACTUAL_COGS_KEY] = null;
      row[ACTUAL_MARKETING_KEY] = null;
      row[ACTUAL_OVERHEAD_KEY] = null;
    };

    const fillCashBalance = (row: ForecastChartRow, monthKey: string) => {
      const point = cashBalanceByMonthKey[monthKey];
      row[CASH_BALANCE_KEY] = point ? Math.round(point.balance) : null;
    };

    const fillProjection = (row: ForecastChartRow, monthKey: string) => {
      if (!projection) {
        row[PROJECTED_CASH_KEY] = null;
        row[SCENARIO_CASH_KEY] = null;
        return;
      }
      const baselineMonth = bakedByMonthKey.get(monthKey);
      const displayMonth = displayByMonthKey.get(monthKey);
      if (monthKey === currentMonthKey) {
        // Anchor at the latest actual balance so the dashed line connects
        // to the historical Cash balance line at "Now".
        row[PROJECTED_CASH_KEY] = bakedProjection?.startingBalance ?? projection.startingBalance;
        row[SCENARIO_CASH_KEY] = scenarioActive
          ? (scenarioApplied?.projection.startingBalance ?? bakedProjection?.startingBalance ?? projection.startingBalance)
          : null;
      } else {
        row[PROJECTED_CASH_KEY] = baselineMonth ? baselineMonth.endingBalance : null;
        row[SCENARIO_CASH_KEY] = scenarioActive
          ? (scenarioByMonthKey.get(monthKey)?.endingBalance ?? null)
          : null;
      }
      const m = displayMonth;
      if (m) {
        const monthMarkers = quoteCoverageByMonthKey.get(monthKey) ?? [];
        const visible = forecastFlowFilterIsActive(flowFilter)
          ? applyForecastFlowFilter({
            items: m.items,
            burn: m.burn,
            quoteMarkers: monthMarkers,
            filter: flowFilter,
          })
          : {
            items: m.items,
            quoteMarkers: monthMarkers,
            arIn: m.arIn,
            apOut: m.apOut,
            plannedOut: m.plannedOut,
            jobOut: m.jobOut || 0,
            burn: m.burn,
          };
        row[PROJ_AR_IN] = visible.arIn;
        row[PROJ_AP_OUT] = visible.apOut;
        row[PROJ_BURN] = visible.burn;
        row[PROJ_PLANNED] = visible.plannedOut;
        row[PROJ_JOB] = visible.jobOut;
        row[PROJ_ESTIMATED] = m.hasEstimatedAr && visible.arIn > 0 ? 1 : 0;
        row[PROJ_MONTH_KEY] = m.monthKey;
        const lineValue = Number(
          scenarioActive ? row[SCENARIO_CASH_KEY] : row[PROJECTED_CASH_KEY],
        ) || 0;
        if (visible.arIn > 0) row[WF_AR_KEY] = [lineValue, lineValue + visible.arIn];
        if (visible.quoteMarkers.length) row[PROJ_QUOTE_MARKERS] = visible.quoteMarkers;
        if (visible.apOut > 0) {
          row[WF_AP_KEY] = [lineValue - visible.apOut, lineValue];
        } else if (visible.quoteMarkers.length) {
          row[WF_AP_KEY] = [lineValue - 1, lineValue];
          row[WF_AP_WARN_ONLY] = true;
        }
        if (visible.burn > 0) {
          row[WF_BURN_KEY] = [lineValue - visible.apOut - visible.burn, lineValue - visible.apOut];
        }
        const prior = displayByMonthKey.get(
          toMonthKey(new Date(m.month.getFullYear(), m.month.getMonth() - 1, 1)),
        );
        if (prior && visible.burn > 0) {
          const priorVisible = applyForecastFlowFilter({
            items: prior.items,
            burn: prior.burn,
            filter: flowFilter,
          });
          row[BURN_DELTA_KEY] = Math.round(visible.burn - priorVisible.burn);
        }
        if (visible.plannedOut > 0) {
          row[WF_PLANNED_KEY] = [
            lineValue - visible.apOut - visible.burn - visible.plannedOut,
            lineValue - visible.apOut - visible.burn,
          ];
        }
        if (visible.jobOut > 0) {
          row[WF_JOB_KEY] = [
            lineValue - visible.apOut - visible.burn - visible.plannedOut - visible.jobOut,
            lineValue - visible.apOut - visible.burn - visible.plannedOut,
          ];
        }
        if (visible.quoteMarkers.length) {
          const peak = quoteWarnAnchorValue(
            [row[WF_AR_KEY], row[WF_AP_KEY], row[WF_BURN_KEY], row[WF_PLANNED_KEY], row[WF_JOB_KEY]],
            lineValue,
          );
          row[WF_WARN_KEY] = [peak, peak];
        }
      }
    };

    if (!showHistoryWindow) {
      return buckets.map((b, i) => {
        const row: ForecastChartRow = { label: b.label, x: i };
        const monthKey = toMonthKey(b.month);
        clearActuals(row);
        fillCashBalance(row, monthKey);
        fillProjection(row, monthKey);
        return row;
      });
    }

    return timeline.map((t, i) => {
      const row: ForecastChartRow = { label: t.label, x: i };
      const overlay = overlayByMonthKey.get(t.monthKey);

      if (showActuals && overlay) fillActuals(row, overlay);
      else clearActuals(row);

      fillCashBalance(row, t.monthKey);
      fillProjection(row, t.monthKey);

      return row;
    });
  }, [
    timeline,
    overlayByMonthKey,
    buckets,
    showActuals,
    showHistoryWindow,
    cashBalanceByMonthKey,
    projection,
    bakedProjection,
    bakedByMonthKey,
    displayByMonthKey,
    scenarioByMonthKey,
    scenarioActive,
    scenarioApplied,
    currentMonthKey,
    quoteCoverageByMonthKey,
    flowFilter,
  ]);

  // ── KPI totals ────────────────────────────────────────────
  const totals = useMemo(() => {
    const sum = buckets.reduce((acc, b) => acc + b.total, 0);
    const payroll = buckets.reduce((acc, b) => acc + b.payrollTotal, 0);
    const overhead = buckets.reduce((acc, b) => acc + b.overheadTotal, 0);
    const avg = buckets.length ? sum / buckets.length : 0;
    const max = buckets.reduce((m, b) => Math.max(m, b.total), 0);
    const peak = buckets.find((b) => b.total === max);
    return { sum, avg, max, payroll, overhead, peakLabel: peak?.label };
  }, [buckets]);

  const actualsAvg = useMemo(() => {
    return averageMonthlyActuals(activeMethodData, 12);
  }, [activeMethodData]);

  // ── Cursor-anchored wheel zoom on the chart ───────────────
  // Visible window as fractions of the full month axis; null = fully zoomed out.
  // Daily bars reuse this camera so swapping row density does not jump the window.
  const [zoomWindow, setZoomWindow] = useState<{ start: number; end: number } | null>(null);
  const [zoomPreset, setZoomPreset] = useState<ForecastZoomPreset>('day');
  const [historicHover, setHistoricHover] = useState<HistoricCashHover | null>(null);
  const historicHoverKey = useRef<string | null>(null);
  const historicTipRef = useRef<HTMLDivElement>(null);
  const onHistoricHover = useCallback((hover: HistoricCashHover | null) => {
    const key = hover?.dayKey ?? null;
    if (historicHoverKey.current === key) {
      const el = historicTipRef.current;
      if (el && hover) {
        el.style.left = `${hover.clientX + 14}px`;
        el.style.top = `${hover.clientY}px`;
      }
      return;
    }
    historicHoverKey.current = key;
    setHistoricHover(hover);
  }, []);
  const [chartWidth, setChartWidth] = useState(800);
  const chartWrapRef = useRef<HTMLDivElement>(null);
  const chartLengthRef = useRef(0);
  const zoomWindowRef = useRef(zoomWindow);
  const zoomRafRef = useRef(0);
  chartLengthRef.current = Math.max(1, nMonths);

  const resetZoom = useCallback(() => {
    const idx = axisMonths.findIndex((m) => m.monthKey === currentMonthKey);
    const today = new Date();
    const day = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const nowX = idx >= 0 ? xForDay(day, idx) : 0;
    const next = runwayZoomWindow(Math.max(1, axisMonths.length), nowX);
    zoomWindowRef.current = next;
    setZoomWindow(next);
    setZoomPreset('month');
  }, [axisMonths, currentMonthKey]);

  const openDayZoom = useCallback(() => {
    const idx = axisMonths.findIndex((m) => m.monthKey === currentMonthKey);
    const today = new Date();
    const day = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const anchorX = xForDay(day, idx >= 0 ? idx : 0);
    const next = zoomWindowAroundAnchor(
      Math.max(1, axisMonths.length),
      FORECAST_DAY_ZOOM_MONTHS,
      anchorX,
      FORECAST_DAY_ANCHOR_FRACTION,
    );
    zoomWindowRef.current = next;
    setZoomWindow(next);
    setZoomPreset('day');
  }, [axisMonths, currentMonthKey]);

  // A new month window changes what the timeline means — start over on Day.
  // Toggling actuals does not, while pod cash is already keeping that window.
  const axisSpanKey = `${axisMonths[0]?.monthKey ?? ''}|${axisMonths.length}|${axisMonths[axisMonths.length - 1]?.monthKey ?? ''}`;
  useLayoutEffect(() => {
    openDayZoom();
  }, [axisSpanKey, openDayZoom]);

  useEffect(() => {
    if (zoomWindow == null) setZoomPreset('month');
  }, [zoomWindow]);

  useLayoutEffect(() => {
    const el = chartWrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const apply = () => setChartWidth(el.clientWidth);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = chartWrapRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      // Take over scrolling only while the cursor is over the graph.
      e.preventDefault();
      const n = Math.max(1, chartLengthRef.current);
      const rect = el.getBoundingClientRect();
      // Anchor on the plot area, excluding the Y axis and right margin.
      const plotLeft = rect.left + CHART_Y_AXIS_PX;
      const plotWidth = Math.max(1, rect.width - CHART_Y_AXIS_PX - CHART_RIGHT_MARGIN_PX);
      const cursorFrac = Math.min(1, Math.max(0, (e.clientX - plotLeft) / plotWidth));

      const prev = zoomWindowRef.current;
      const start = prev?.start ?? 0;
      const end = prev?.end ?? 1;
      const span = Math.max(1e-6, end - start);

      let ns: number;
      let ne: number;
      // Pinch (ctrl+wheel) and vertical scroll zoom; horizontal scroll pans.
      const isPan = !e.ctrlKey && Math.abs(e.deltaX) > Math.abs(e.deltaY);
      if (isPan) {
        if (!prev) return;
        const shift = (e.deltaX / plotWidth) * span;
        ns = start + shift;
        ne = end + shift;
      } else {
        let dy = e.deltaY;
        if (e.deltaMode === 1) dy *= 16;
        else if (e.deltaMode === 2) dy *= plotWidth;
        const clamped = Math.max(-80, Math.min(80, dy));
        const factor = Math.exp(clamped * 0.00115);
        const minSpan = Math.min(1, FORECAST_MIN_ZOOM_MONTHS / n);
        const newSpan = Math.min(1, Math.max(minSpan, span * factor));
        const anchor = start + cursorFrac * span;
        ns = anchor - cursorFrac * newSpan;
        ne = ns + newSpan;
      }

      if (ns < 0) { ne -= ns; ns = 0; }
      if (ne > 1) { ns -= ne - 1; ne = 1; }
      ns = Math.max(0, Math.min(ns, 1));
      ne = Math.max(ns, Math.min(ne, 1));
      const next = ns <= 0.0005 && ne >= 0.9995 ? null : { start: ns, end: ne };
      zoomWindowRef.current = next;
      // One React/Recharts layout per frame — trackpads fire faster than that.
      if (zoomRafRef.current) return;
      zoomRafRef.current = requestAnimationFrame(() => {
        zoomRafRef.current = 0;
        setZoomWindow(zoomWindowRef.current);
      });
    };

    // React's synthetic wheel handlers are passive — a native non-passive
    // listener is required for preventDefault to stop the page scrolling.
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', handleWheel);
      if (zoomRafRef.current) cancelAnimationFrame(zoomRafRef.current);
    };
  }, []);

  const visibleMonths = visibleMonthSpan(zoomWindow, nMonths);
  const chartGrain: ForecastChartGrain = zoomWindow == null
    ? 'monthly'
    : forecastChartGrainFromPreset(zoomPreset);

  const applyZoomPreset = useCallback((preset: ForecastZoomPreset) => {
    if (preset === 'month') {
      resetZoom();
      return;
    }
    const idx = axisMonths.findIndex((m) => m.monthKey === currentMonthKey);
    const monthIndex = idx >= 0 ? idx : 0;
    const anchorX = xForDay(forecastToday, monthIndex);
    const spanMonths = preset === 'day' ? FORECAST_DAY_ZOOM_MONTHS : FORECAST_WEEK_ZOOM_MONTHS;
    const next = zoomWindowAroundAnchor(
      nMonths,
      spanMonths,
      anchorX,
      preset === 'day' ? FORECAST_DAY_ANCHOR_FRACTION : 0.5,
    );
    zoomWindowRef.current = next;
    setZoomPreset(preset);
    setZoomWindow(next);
  }, [axisMonths, currentMonthKey, forecastToday, nMonths, resetZoom]);

  const displayDayBuckets = useMemo(
    () => buildForecastDayBuckets(displayProjection?.months ?? [], forecastToday, quoteCoverageMarkers),
    [displayProjection, forecastToday, quoteCoverageMarkers],
  );

  const overheadByDayKey = useMemo(() => {
    const months = displayProjection?.months ?? bakedProjection?.months ?? [];
    if (months.length === 0) return new Map<string, number>();
    const headcount = new Map<string, number>();
    for (const bucket of buckets) {
      headcount.set(toMonthKey(bucket.month), bucket.activeHeadcount || 0);
    }
    return buildOverheadBurnByDayKey(overhead, months, forecastToday, headcount);
  }, [overhead, displayProjection, bakedProjection, forecastToday, buckets]);

  const displayDailyWalk = useMemo(
    () => walkForecastDailyBalances({
      months: displayProjection?.months ?? [],
      startingBalance: displayProjection?.startingBalance ?? 0,
      today: forecastToday,
      dayBuckets: displayDayBuckets,
      payrollSchedule,
      overheadByDayKey,
    }),
    [displayProjection, forecastToday, displayDayBuckets, payrollSchedule, overheadByDayKey],
  );

  const bakedDayBuckets = useMemo(
    () => buildForecastDayBuckets(bakedProjection?.months ?? [], forecastToday),
    [bakedProjection, forecastToday],
  );

  const bakedDailyWalk = useMemo(
    () => walkForecastDailyBalances({
      months: bakedProjection?.months ?? [],
      startingBalance: bakedProjection?.startingBalance ?? 0,
      today: forecastToday,
      dayBuckets: bakedDayBuckets,
      payrollSchedule,
      overheadByDayKey,
    }),
    [bakedProjection, forecastToday, bakedDayBuckets, payrollSchedule, overheadByDayKey],
  );

  const scenarioDailyWalk = useMemo(
    () => (scenarioActive && scenarioApplied
      ? walkForecastDailyBalances({
        months: scenarioApplied.projection.months,
        startingBalance: scenarioApplied.projection.startingBalance,
        today: forecastToday,
        payrollSchedule,
        overheadByDayKey,
      })
      : null),
    [scenarioActive, scenarioApplied, forecastToday, payrollSchedule, overheadByDayKey],
  );

  const displayDailyByKey = useMemo(
    () => new Map(displayDailyWalk.map((point) => [point.dayKey, point])),
    [displayDailyWalk],
  );

  const displayWeeklyWalk = useMemo(
    () => rollDailyWalkToWeeks(displayDailyWalk),
    [displayDailyWalk],
  );
  const bakedWeeklyWalk = useMemo(
    () => rollDailyWalkToWeeks(bakedDailyWalk),
    [bakedDailyWalk],
  );
  const scenarioWeeklyWalk = useMemo(
    () => (scenarioDailyWalk ? rollDailyWalkToWeeks(scenarioDailyWalk) : null),
    [scenarioDailyWalk],
  );
  const displayWeeklyByKey = useMemo(
    () => new Map(displayWeeklyWalk.map((point) => [point.weekKey, point])),
    [displayWeeklyWalk],
  );

  const dailyChartData = useMemo(() => {
    if (chartGrain !== 'daily') return [] as ForecastChartRow[];
    const range = zoomWindowToDayRange(zoomWindow, nMonths, monthStarts);
    if (!range) return [] as ForecastChartRow[];
    return buildDailyForecastChartSources({
      axisMonthKeys: axisMonths.map((m) => m.monthKey),
      monthStarts,
      fromDay: range.fromDay,
      toDay: range.toDay,
      today: forecastToday,
      displayWalk: displayDailyWalk,
      bakedWalk: bakedDailyWalk,
      scenarioWalk: scenarioDailyWalk,
      sequenceByDay,
      scenarioActive,
    }).map((src) => {
      const point = displayDailyByKey.get(src.dayKey);
      return mapPeriodSourceToChartRow(
        withForecastFlowFilter(src, point?.items, flowFilter),
        scenarioActive,
        false,
      );
    });
  }, [
    axisMonths,
    bakedDailyWalk,
    chartGrain,
    displayDailyByKey,
    displayDailyWalk,
    monthStarts,
    nMonths,
    scenarioActive,
    scenarioDailyWalk,
    sequenceByDay,
    forecastToday,
    zoomWindow,
    flowFilter,
  ]);

  const weeklyChartData = useMemo(() => {
    if (chartGrain !== 'weekly') return [] as ForecastChartRow[];
    const range = zoomWindowToDayRange(zoomWindow, nMonths, monthStarts);
    if (!range) return [] as ForecastChartRow[];
    return buildWeeklyForecastChartSources({
      axisMonthKeys: axisMonths.map((m) => m.monthKey),
      monthStarts,
      fromDay: range.fromDay,
      toDay: range.toDay,
      today: forecastToday,
      displayWalk: displayWeeklyWalk,
      bakedWalk: bakedWeeklyWalk,
      scenarioWalk: scenarioWeeklyWalk,
      sequenceByDay,
      scenarioActive,
    }).map((src) => {
      const point = displayWeeklyByKey.get(src.weekKey);
      return stripChartRowCash(mapPeriodSourceToChartRow(
        withForecastFlowFilter(src, point?.items, flowFilter),
        scenarioActive,
        true,
      ));
    });
  }, [
    axisMonths,
    bakedWeeklyWalk,
    chartGrain,
    displayWeeklyByKey,
    displayWeeklyWalk,
    monthStarts,
    nMonths,
    scenarioActive,
    scenarioWeeklyWalk,
    sequenceByDay,
    forecastToday,
    zoomWindow,
    flowFilter,
  ]);

  const historicalCashLineData = useMemo(
    () => historicalCashOverlayRows(historicalSequenceChartPoints({
      axisMonthKeys: axisMonths.map((m) => m.monthKey),
      sequenceByDay,
      today: forecastToday,
    })),
    [axisMonths, forecastToday, sequenceByDay],
  );

  const chartData = chartGrain === 'daily'
    ? dailyChartData
    : chartGrain === 'weekly'
      ? weeklyChartData
      : monthlyChartData.map(stripChartRowCash);

  const showHistoricCashPath = chartGrain !== 'daily' && hasCashBalanceData && historicalCashLineData.length > 1;

  const flowBarSize = chartGrain === 'daily'
    ? dailyChartBarSize(
      Math.max(1, chartWidth - CHART_Y_AXIS_PX - CHART_RIGHT_MARGIN_PX),
      Math.max(1, Math.round(visibleMonths * FORECAST_AVG_DAYS_PER_MONTH)),
    )
    : chartGrain === 'weekly'
      ? dailyChartBarSize(
        Math.max(1, chartWidth - CHART_Y_AXIS_PX - CHART_RIGHT_MARGIN_PX),
        Math.max(1, Math.round(visibleMonths * FORECAST_AVG_DAYS_PER_MONTH / 7)),
      )
      : 12;

  // ── Click a month or day → itemized AR/AP breakdown panel ────────
  const [inspectedMonthKey, setInspectedMonthKey] = useState<string | null>(null);
  const [inspectedDayKey, setInspectedDayKey] = useState<string | null>(null);
  const [inspectedWeekKey, setInspectedWeekKey] = useState<string | null>(null);
  const [expandedDetailGroups, setExpandedDetailGroups] = useState<Set<string>>(new Set());
  const [numbersTableOpen, setNumbersTableOpen] = useState(false);
  const numbersTableRef = useRef<HTMLDivElement>(null);
  const openBurnDetail = useCallback(() => {
    setNumbersTableOpen(true);
    window.setTimeout(() => {
      numbersTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 40);
  }, []);
  const openCockpit = useCallback(() => {
    if (canViewObligations) {
      setBonusDraft(obligations.bonusFor(obligationYear));
      setTaxDraft(obligations.taxFor(obligationYear));
      setSettingsDraft(obligations.settings);
    }
    setCockpitOpen(true);
  }, [canViewObligations, obligationYear, obligations]);
  const detailCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setExpandedDetailGroups(new Set([
      'section:projects',
      'section:burn_pos_tax',
      'section:planned',
      'section:capex',
    ]));
  }, [inspectedMonthKey, inspectedDayKey, inspectedWeekKey]);

  useEffect(() => {
    if (!inspectedMonthKey && !inspectedDayKey && !inspectedWeekKey) return;
    detailCardRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [inspectedMonthKey, inspectedDayKey, inspectedWeekKey]);

  useEffect(() => {
    if (chartGrain === 'daily') {
      setInspectedMonthKey(null);
      setInspectedWeekKey(null);
    } else if (chartGrain === 'weekly') {
      setInspectedMonthKey(null);
      setInspectedDayKey(null);
    } else {
      setInspectedDayKey(null);
      setInspectedWeekKey(null);
    }
  }, [chartGrain]);

  const handleChartClick = useCallback(
    (state: {
      activeLabel?: string | number;
      activePayload?: Array<{ payload?: ForecastChartRow }>;
    } | null) => {
      const activeX = state?.activeLabel;
      const fromPayload = state?.activePayload?.[0]?.payload;
      const fromX =
        typeof activeX === 'number'
          ? chartData.reduce<ForecastChartRow | undefined>((best, row) => {
            if (typeof row.x !== 'number') return best;
            if (!best || typeof best.x !== 'number') return row;
            return Math.abs(row.x - activeX) < Math.abs(best.x - activeX)
              ? row
              : best;
          }, undefined)
          : chartData.find((r) => r.label === String(activeX ?? ''));
      const row = fromPayload ?? fromX;
      if (chartGrain === 'daily') {
        const key = row?.[PROJ_DAY_KEY];
        if (typeof key !== 'string' || row?.[PROJ_DAY_HAS_DETAIL] !== true) return;
        setInspectedDayKey((prev) => (prev === key ? null : key));
        setInspectedMonthKey(null);
        setInspectedWeekKey(null);
      } else if (chartGrain === 'weekly') {
        const key = row?.[PROJ_WEEK_KEY];
        if (typeof key !== 'string' || row?.[PROJ_DAY_HAS_DETAIL] !== true) return;
        setInspectedWeekKey((prev) => (prev === key ? null : key));
        setInspectedMonthKey(null);
        setInspectedDayKey(null);
      } else {
        const key = row?.[PROJ_MONTH_KEY];
        if (typeof key !== 'string') return;
        setInspectedMonthKey((prev) => (prev === key ? null : key));
        setInspectedDayKey(null);
        setInspectedWeekKey(null);
      }
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && chartWrapRef.current?.contains(focused)) {
        focused.blur();
      }
    },
    [chartData, chartGrain],
  );

  const inspectedMonth = useMemo(
    () => (inspectedMonthKey ? displayByMonthKey.get(inspectedMonthKey) ?? null : null),
    [inspectedMonthKey, displayByMonthKey],
  );

  const inspectedQuoteMarkers = useMemo(
    () => (inspectedMonthKey ? quoteCoverageByMonthKey.get(inspectedMonthKey) ?? [] : []),
    [inspectedMonthKey, quoteCoverageByMonthKey],
  );

  const inspectedMonthVisible = useMemo(
    () => applyForecastFlowFilter({
      items: inspectedMonth?.items ?? [],
      burn: inspectedMonth?.burn ?? 0,
      quoteMarkers: inspectedQuoteMarkers,
      filter: flowFilter,
    }),
    [inspectedMonth, inspectedQuoteMarkers, flowFilter],
  );

  const inspectedInGroups = useMemo(
    () => groupForecastItemsByProject(inspectedMonthVisible.items.filter((i) => i.kind === 'ar')),
    [inspectedMonthVisible],
  );

  const inspectedDay = inspectedDayKey ? displayDailyByKey.get(inspectedDayKey) ?? null : null;
  const inspectedWeek = inspectedWeekKey ? displayWeeklyByKey.get(inspectedWeekKey) ?? null : null;
  const inspectedSlice = inspectedDay ?? inspectedWeek;

  const inspectedSliceVisible = useMemo(
    () => applyForecastFlowFilter({
      items: inspectedSlice?.items ?? [],
      burn: inspectedSlice?.burn ?? 0,
      quoteMarkers: inspectedSlice?.quoteMarkers ?? [],
      filter: flowFilter,
    }),
    [inspectedSlice, flowFilter],
  );

  const inspectedBurnDelta = useMemo(() => {
    if (!inspectedMonth || inspectedMonthVisible.burn <= 0) return null;
    const prior = displayByMonthKey.get(
      toMonthKey(new Date(inspectedMonth.month.getFullYear(), inspectedMonth.month.getMonth() - 1, 1)),
    );
    if (!prior) return null;
    const priorBurn = applyForecastFlowFilter({
      items: prior.items,
      burn: prior.burn,
      filter: flowFilter,
    }).burn;
    return Math.round(inspectedMonthVisible.burn - priorBurn);
  }, [inspectedMonth, inspectedMonthVisible, displayByMonthKey, flowFilter]);

  const moneyOutBillCategory = useCallback((item: { billId?: string }) => {
    if (!item.billId) return null;
    return billsById.get(item.billId)?.category ?? null;
  }, [billsById]);

  const inspectedOutSections = useMemo(() => {
    const burnDeltaNote =
      inspectedBurnDelta != null && Math.abs(inspectedBurnDelta) >= BURN_DELTA_MIN
        ? `${inspectedBurnDelta > 0 ? '↑' : '↓'} ${formatCurrency(Math.abs(inspectedBurnDelta))} vs prior month`
        : undefined;
    const items = inspectedMonthVisible.items.filter((i) => i.kind === 'ap' || i.kind === 'job' || i.kind === 'planned');
    const burn = inspectedMonthVisible.burn > 0
      ? {
          amount: inspectedMonthVisible.burn,
          label: 'Overhead + payroll burn',
          sublabel: ['Monthly burn forecast', burnDeltaNote].filter(Boolean).join(' · '),
        }
      : null;
    const narrowed = selectMoneyOutForKindFilter({
      items,
      burn,
      opexBudgetMonthlyByGroup,
      quoteMarkers: inspectedMonthVisible.quoteMarkers,
      selected: moneyOutTags,
      categoryFor: moneyOutBillCategory,
    });
    return buildForecastMoneyOutSections(narrowed);
  }, [inspectedMonthVisible, inspectedBurnDelta, opexBudgetMonthlyByGroup, moneyOutTags, moneyOutBillCategory]);

  const inspectedDayInGroups = useMemo(
    () => groupForecastItemsByProject(inspectedSliceVisible.items.filter((i) => i.kind === 'ar')),
    [inspectedSliceVisible],
  );

  const inspectedDayOutSections = useMemo(() => {
    const items = inspectedSliceVisible.items.filter((i) => i.kind === 'ap' || i.kind === 'job' || i.kind === 'planned');
    const burn = inspectedSliceVisible.burn > 0
      ? {
          amount: inspectedSliceVisible.burn,
          label: 'Allocated burn',
          sublabel: inspectedWeek
            ? 'This week’s share of overhead + payroll'
            : 'This day’s share of overhead + payroll',
        }
      : null;
    const narrowed = selectMoneyOutForKindFilter({
      items,
      burn,
      quoteMarkers: inspectedSliceVisible.quoteMarkers,
      selected: moneyOutTags,
      categoryFor: moneyOutBillCategory,
    });
    return buildForecastMoneyOutSections(narrowed);
  }, [inspectedSliceVisible, inspectedWeek, moneyOutTags, moneyOutBillCategory]);

  const moneyOutTagsPresent = useMemo(() => {
    const source = inspectedSlice ? inspectedSliceVisible : inspectedMonthVisible;
    return moneyOutFilterTagsPresent({
      items: (source?.items ?? []).filter((i) => i.kind === 'ap' || i.kind === 'job' || i.kind === 'planned'),
      burn: source?.burn ?? 0,
      categoryFor: moneyOutBillCategory,
    });
  }, [inspectedSlice, inspectedSliceVisible, inspectedMonthVisible, moneyOutBillCategory]);

  const moneyOutDisplayedTotal = (inspectedSlice ? inspectedDayOutSections : inspectedOutSections)
    .reduce((sum, section) => sum + section.total, 0);

  const toggleDetailGroup = useCallback((key: string) => {
    setExpandedDetailGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const xDomain = useMemo<[number, number]>(() => {
    const n = Math.max(1, nMonths);
    if (n <= 1) return [-0.5, 0.5];
    if (!zoomWindow) return [-0.5, n - 0.5];
    return [zoomWindow.start * n - 0.5, zoomWindow.end * n - 0.5];
  }, [nMonths, zoomWindow]);

  const xTicks = useMemo(() => {
    const n = Math.max(1, nMonths);
    const lo = xDomain[0];
    const hi = xDomain[1];
    if (chartGrain === 'daily' || chartGrain === 'weekly') {
      const start = dateFromMonthX(Math.max(0, lo), monthStarts);
      const end = dateFromMonthX(Math.min(n - 1e-9, hi), monthStarts);
      if (!start || !end) return [];
      const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      const indexByKey = new Map(axisMonths.map((m, i) => [m.monthKey, i]));
      const ticks: number[] = [];
      if (chartGrain === 'weekly') {
        const weeks: Date[] = [];
        let cur = startOfWeekSunday(start);
        while (cur <= last) {
          weeks.push(new Date(cur));
          cur = addLocalDays(cur, 7);
        }
        const step = weeks.length <= 12 ? 1 : Math.ceil(weeks.length / 12);
        for (let i = 0; i < weeks.length; i += step) {
          const plotDay = weekPlotDay(weeks[i]);
          const idx = indexByKey.get(toMonthKey(plotDay)) ?? indexByKey.get(toMonthKey(weeks[i]));
          if (idx == null) continue;
          ticks.push(xForDay(indexByKey.get(toMonthKey(plotDay)) != null ? plotDay : weeks[i], idx));
        }
        return ticks;
      }
      const days: Date[] = [];
      const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      while (cur <= last) {
        days.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
      }
      const step = days.length <= 12 ? 1 : Math.ceil(days.length / 12);
      for (let i = 0; i < days.length; i += step) {
        const day = days[i];
        const idx = indexByKey.get(toMonthKey(day));
        if (idx == null) continue;
        ticks.push(xForDay(day, idx));
      }
      return ticks;
    }
    const first = Math.max(0, Math.ceil(lo));
    const last = Math.min(n - 1, Math.floor(hi));
    const span = Math.max(1, last - first + 1);
    const step = span <= 12 ? 1 : Math.ceil(span / 12);
    const ticks: number[] = [];
    for (let i = first; i <= last; i += step) ticks.push(i);
    return ticks;
  }, [axisMonths, chartGrain, monthStarts, nMonths, xDomain]);

  const formatXTick = useCallback(
    (v: number) => {
      if (chartGrain === 'daily') {
        const day = dateFromMonthX(v, monthStarts);
        return day ? day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
      }
      if (chartGrain === 'weekly') {
        const day = dateFromMonthX(v, monthStarts);
        if (!day) return '';
        return startOfWeekSunday(day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      return axisMonths[Math.round(v)]?.label ?? monthlyChartData[Math.round(v)]?.label ?? '';
    },
    [axisMonths, chartGrain, monthStarts, monthlyChartData],
  );

  const yDomain = useMemo<[number, number] | undefined>(() => {
    if (chartData.length === 0) return undefined;
    const lo = zoomWindow ? xDomain[0] - 1 : Number.NEGATIVE_INFINITY;
    const hi = zoomWindow ? xDomain[1] + 1 : Number.POSITIVE_INFINITY;
    let max = 0;
    let min = 0;
    const consider = (v: unknown) => {
      const take = (n: number) => {
        if (!Number.isFinite(n)) return;
        if (n > max) max = n;
        if (n < min) min = n;
      };
      if (typeof v === 'number') take(v);
      else if (Array.isArray(v) && v.length >= 2) {
        take(Number(v[0]));
        take(Number(v[1]));
      }
    };
    for (const row of chartData) {
      const x = typeof row.x === 'number' ? row.x : 0;
      if (x < lo || x > hi) continue;
      consider(row[ACTUAL_REVENUE_KEY]);
      consider(row[ACTUAL_COGS_KEY]);
      consider(row[ACTUAL_MARKETING_KEY]);
      consider(row[ACTUAL_OVERHEAD_KEY]);
      consider(row[CASH_BALANCE_KEY]);
      consider(row[PROJECTED_CASH_KEY]);
      consider(row[SCENARIO_CASH_KEY]);
      consider(row[WF_AR_KEY]);
      consider(row[WF_AP_KEY]);
      consider(row[WF_BURN_KEY]);
      consider(row[WF_PLANNED_KEY]);
      consider(row[WF_JOB_KEY]);
    }
    if (chartGrain !== 'daily') {
      for (const row of historicalCashLineData) {
        const x = typeof row.x === 'number' ? row.x : 0;
        if (x < lo || x > hi) continue;
        consider(row[CASH_BALANCE_KEY]);
      }
    }
    const pad = Math.max(max * 0.08, 1000);
    // A $1 quote-warn anchor can sit just under a zero balance. That is not a real dip.
    const floor = min < -1 ? forecastNegativeAxisFloor(min) : null;
    if (floor == null) return [0, max + pad];
    return [floor, max + pad];
  }, [chartData, chartGrain, historicalCashLineData, xDomain, zoomWindow]);

  const yTicks = useMemo(
    () => (yDomain ? forecastYAxisTicks(yDomain[0], yDomain[1]) : undefined),
    [yDomain],
  );

  const zoomRangeLabel = useMemo(() => {
    if (!zoomWindow || nMonths <= 0) return null;
    if (chartGrain === 'daily' || chartGrain === 'weekly') {
      const first = dateFromMonthX(zoomWindow.start * nMonths, monthStarts);
      const last = dateFromMonthX(Math.min(nMonths - 1e-9, zoomWindow.end * nMonths - 1e-9), monthStarts);
      if (!first || !last) return null;
      const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const a = fmt(first);
      const b = fmt(last);
      return a === b ? a : `${a} – ${b}`;
    }
    const firstIdx = Math.min(nMonths - 1, Math.max(0, Math.ceil(zoomWindow.start * nMonths - 1e-6)));
    const lastIdx = Math.min(nMonths - 1, Math.max(0, Math.floor(zoomWindow.end * nMonths - 1e-6)));
    const first = axisMonths[firstIdx]?.label;
    const last = axisMonths[lastIdx]?.label;
    if (!first || !last) return null;
    return first === last ? first : `${first} – ${last}`;
  }, [axisMonths, chartGrain, monthStarts, nMonths, zoomWindow]);

  const currentMonthX = useMemo(() => {
    const idx = axisMonths.findIndex((m) => m.monthKey === currentMonthKey);
    if (idx < 0) return null;
    // Month bars stay on the month tick, but Now is today. Parking Now at
    // idx (month start) made the daily Sequence line run past the marker.
    return xForDay(forecastToday, idx);
  }, [axisMonths, currentMonthKey, forecastToday]);

  const projectedCashFromNowPoints = useMemo(() => {
    if (currentMonthX == null) return [] as Array<{ x: number; y: number }>;
    const lastHistoric = historicalCashLineData[historicalCashLineData.length - 1];
    const fromHistoricLine = typeof lastHistoric?.[CASH_BALANCE_KEY] === 'number'
      ? lastHistoric[CASH_BALANCE_KEY]
      : null;
    const lastDailyActual = chartGrain === 'daily'
      ? chartData.reduce<{ x: number; cash: number } | null>((best, row) => {
        const cash = row[CASH_BALANCE_KEY];
        if (typeof row.x !== 'number' || typeof cash !== 'number' || !Number.isFinite(cash)) return best;
        if (!best || row.x > best.x) return { x: row.x, cash };
        return best;
      }, null)?.cash ?? null
      : null;
    const fromHistoric = fromHistoricLine ?? lastDailyActual;
    const fallback = bakedProjection?.startingBalance ?? projection?.startingBalance ?? null;
    return joinProjectedCashAtNow({
      nowX: currentMonthX,
      nowCash: fromHistoric ?? (typeof fallback === 'number' ? Math.round(fallback) : null),
      rows: chartData.map((row) => ({
        x: typeof row.x === 'number' ? row.x : Number.NaN,
        cash: typeof row[PROJECTED_CASH_KEY] === 'number' ? Number(row[PROJECTED_CASH_KEY]) : null,
      })),
    });
  }, [
    bakedProjection?.startingBalance,
    chartData,
    chartGrain,
    currentMonthX,
    historicalCashLineData,
    projection?.startingBalance,
  ]);

  const scenarioCashFromNowPoints = useMemo(() => {
    if (!scenarioActive || currentMonthX == null) {
      return [] as Array<{ x: number; y: number }>;
    }
    const last = historicalCashLineData[historicalCashLineData.length - 1];
    const fromHistoric = typeof last?.[CASH_BALANCE_KEY] === 'number' ? last[CASH_BALANCE_KEY] : null;
    const fallback = scenarioApplied?.projection.startingBalance
      ?? bakedProjection?.startingBalance
      ?? projection?.startingBalance
      ?? null;
    return joinProjectedCashAtNow({
      nowX: currentMonthX,
      nowCash: fromHistoric ?? (typeof fallback === 'number' ? Math.round(fallback) : null),
      rows: chartData.map((row) => ({
        x: typeof row.x === 'number' ? row.x : Number.NaN,
        cash: typeof row[SCENARIO_CASH_KEY] === 'number' ? Number(row[SCENARIO_CASH_KEY]) : null,
      })),
    });
  }, [
    bakedProjection?.startingBalance,
    chartData,
    chartGrain,
    currentMonthX,
    historicalCashLineData,
    projection?.startingBalance,
    scenarioActive,
    scenarioApplied?.projection.startingBalance,
  ]);

  const overlayProjectedFromNow = projectedCashFromNowPoints.length > 1;

  // ── Render ────────────────────────────────────────────────
  return (
    <ForecastBillMetaContext.Provider value={billMetaCtx}>
    <div>
      <div className="fin-forecast-controls">
        <div className="fin-forecast-toggle" role="group" aria-label="Window in months">
          {HORIZONS.map(h => (
            <button
              key={h}
              type="button"
              className={horizon === h ? 'active' : ''}
              onClick={() => setHorizon(h)}
              aria-pressed={horizon === h}
            >
              {h} mo
            </button>
          ))}
        </div>

        <div
          className="fin-segmented"
          role="group"
          aria-label="Accounting method"
          style={{ marginLeft: 'auto', opacity: hasActualsData ? 1 : 0.5 }}
        >
          <button
            type="button"
            className={method === 'cash' ? 'active' : ''}
            onClick={() => setMethod('cash')}
            aria-pressed={method === 'cash'}
            disabled={!hasActualsData}
          >
            Cash
          </button>
          <button
            type="button"
            className={method === 'accrual' ? 'active' : ''}
            onClick={() => setMethod('accrual')}
            aria-pressed={method === 'accrual'}
            disabled={!hasActualsData}
          >
            Accrual
          </button>
        </div>

        <HoverTip
          wide
          tip={
            <div className="fin-refresh-tip">
              <strong>Refresh cash sources</strong>
              <p className="fin-refresh-tip-lead">
                Pulls QuickBooks history and Sequence cash together.
              </p>
              <div className="fin-refresh-tip-row">
                <span className="fin-refresh-tip-label">QuickBooks</span>
                <span className={`fin-refresh-tip-status${refreshing ? ' is-busy' : cache?.lastSyncedAt ? ' is-ok' : ''}`}>
                  {refreshing
                    ? 'Refreshing…'
                    : cache?.lastSyncedAt
                      ? `As of ${formatDateTime(cache.lastSyncedAt)}`
                      : cacheLoaded
                        ? 'No snapshot yet'
                        : 'Loading…'}
                </span>
                {cache?.rangeStart && cache.rangeEnd && !refreshing && (
                  <span className="fin-refresh-tip-meta">{cache.rangeStart} → {cache.rangeEnd}</span>
                )}
              </div>
              <div className="fin-refresh-tip-row">
                <span className="fin-refresh-tip-label">Sequence cash</span>
                <span className={`fin-refresh-tip-status${podSyncing ? ' is-busy' : hasCashBalanceData ? ' is-ok' : ''}`}>
                  {podSyncing
                    ? 'Syncing…'
                    : hasCashBalanceData && dailyReport?.dateKey
                      ? `${formatCurrency(dailyReport.sequencePodAmount)} · ${dailyReport.dateKey}`
                      : 'No pod snapshot yet'}
                </span>
                {dailyReport?.updatedAt && !podSyncing && (
                  <span className="fin-refresh-tip-meta">Synced {formatDateTime(dailyReport.updatedAt)}</span>
                )}
                <span className="fin-refresh-tip-meta">Otherwise 8 AM &amp; 2 PM Central</span>
              </div>
              {(refreshNotice || refreshError || snapshotError) && <div className="bp-tip-divider" />}
              {refreshNotice && <span className="fin-refresh-tip-status is-ok">{refreshNotice}</span>}
              {snapshotError && <span className="fin-refresh-tip-status is-err">{snapshotError}</span>}
              {refreshError && <span className="fin-refresh-tip-status is-err">{refreshError}</span>}
              <div className="bp-tip-divider" />
              <span className="bp-tip-hint">
                Click to refresh both now.
              </span>
            </div>
          }
        >
          <button
            type="button"
            className="fin-secondary-btn fin-refresh-icon-btn"
            onClick={onRefreshAll}
            disabled={syncingSources}
            aria-label="Refresh QuickBooks history and Sequence pod cash"
            aria-busy={syncingSources}
          >
            <RefreshCw size={14} className={syncingSources ? 'fin-refresh-spin' : undefined} />
          </button>
        </HoverTip>

        <HoverTip
          wide
          tip={
            <div className="fin-refresh-tip">
              <strong>CapEx</strong>
              <p className="fin-refresh-tip-lead">
                Sandbox purchases, extra payments, and timing shifts on the live cash line. Nothing writes bills or Sequence.
              </p>
              {stackedPlans.length > 0 ? (
                <div className="fin-refresh-tip-row">
                  <span className="fin-refresh-tip-label">On the chart</span>
                  <span className="fin-refresh-tip-status">
                    {stackedPlans.map((p) => p.title).join(' + ')}
                  </span>
                  <span className="fin-refresh-tip-meta">
                    {stackedPlans.length === 1
                      ? `${CAPITAL_PLAN_STATUS_LABELS[stackedPlans[0].status]}${stackedPlans[0].updatedByName ? ` · ${stackedPlans[0].updatedByName}` : ''}`
                      : `${stackedPlans.length} plans stacked`}
                  </span>
                </div>
              ) : workspace.activePlan ? (
                <div className="fin-refresh-tip-row">
                  <span className="fin-refresh-tip-label">Open plan</span>
                  <span className="fin-refresh-tip-status">{workspace.activePlan.title}</span>
                </div>
              ) : (
                <div className="fin-refresh-tip-row">
                  <span className="fin-refresh-tip-label">Session</span>
                  <span className="fin-refresh-tip-status">
                    {enabledLineCount > 0
                      ? `${enabledLineCount} unsaved line${enabledLineCount === 1 ? '' : 's'}`
                      : 'No saved plan — start a new overlay'}
                  </span>
                </div>
              )}
              {projection && (
                <div className="fin-refresh-tip-row">
                  <span className="fin-refresh-tip-label">Cash</span>
                  <span className={`fin-refresh-tip-status${(scenarioApplied?.projection.lowestBalance ?? projection.lowestBalance ?? 0) < 0 ? ' is-err' : ' is-ok'}`}>
                    Lowest {formatCurrency(scenarioApplied?.projection.lowestBalance ?? projection.lowestBalance ?? 0)}
                    {scenarioActive && projection.lowestBalance != null && scenarioApplied?.projection.lowestBalance != null && (
                      <> · live {formatCurrency(projection.lowestBalance)}</>
                    )}
                  </span>
                  <span className="fin-refresh-tip-meta">
                    {(scenarioApplied?.projection.runwayMonths ?? projection.runwayMonths) != null
                      ? `Runway ${scenarioApplied?.projection.runwayMonths ?? projection.runwayMonths} mo`
                      : `Stays positive over ${horizon} mo`}
                  </span>
                </div>
              )}
              {!projection && (
                <span className="fin-refresh-tip-status is-err">
                  {projectionUnavailableReason || 'Cash projection is not ready yet.'}
                </span>
              )}
              <div className="bp-tip-divider" />
              <span className="bp-tip-hint">
                {projection ? 'Click to open the planner.' : 'Refresh Sequence pod cash, then try again.'}
              </span>
            </div>
          }
        >
          <button
            type="button"
            className="fin-primary-btn"
            onClick={() => setPlanningOpen(true)}
            disabled={!projection}
            aria-label="Open CapEx cash plan"
          >
            <SlidersHorizontal size={13} />
            CapEx
          </button>
        </HoverTip>

        <HoverTip
          wide
          tip={
            <div className="fin-refresh-tip">
              <strong>Cockpit</strong>
              <p className="fin-refresh-tip-lead">
                Cash floor, owner pay, and debt.
                {canViewObligations ? ' Admins also get bonuses and tax in the sidebar.' : ''}
              </p>
              <span className="bp-tip-hint">Click to open the cockpit.</span>
            </div>
          }
        >
          <button
            type="button"
            className="fin-primary-btn"
            onClick={() => {
              if (canViewObligations) {
                setBonusDraft(obligations.bonusFor(obligationYear));
                setTaxDraft(obligations.taxFor(obligationYear));
                setSettingsDraft(obligations.settings);
              }
              setCockpitOpen(true);
            }}
            aria-label="Open cockpit"
          >
            <LayoutDashboard size={13} />
            Cockpit
          </button>
        </HoverTip>
      </div>

      {(refreshError || snapshotError || projArError || projApError) && (
        <div className="fin-forecast-meta" style={{ marginBottom: 12 }}>
          {snapshotError && <div className="fin-balance-error">{snapshotError}</div>}
          {refreshError && <div className="fin-balance-error">{refreshError}</div>}
          {projArError && <div className="fin-balance-error">{projArError}</div>}
          {projApError && <div className="fin-balance-error">{projApError}</div>}
        </div>
      )}

      {projectionUnavailableReason && (
        <div className="fin-forecast-meta" style={{ marginBottom: 12 }}>
          <span className="fin-balance-meta">{projectionUnavailableReason}</span>
        </div>
      )}

      <div className="fin-overview-grid">
        <HoverTip
          wide
          block
          tip={(
            <div className="fin-refresh-tip">
              <strong>Total burn over {horizon} mo</strong>
              <p className="fin-refresh-tip-lead">
                Loaded payroll ({formatCurrency(totals.payroll)}) plus recurring overhead and budgeted operating expenses ({formatCurrency(totals.overhead)}), added across the next {horizon} months.
                Supplier bills, project costs, CapEx, bonuses, and tax are not in this number.
              </p>
            </div>
          )}
        >
          <div className="fin-kpi-card">
            <span className="fin-kpi-label">Total burn over {horizon} mo</span>
            <span className="fin-kpi-value">{formatCurrency(totals.sum)}</span>
            <span className="fin-kpi-sub">Cumulative overhead + payroll.</span>
          </div>
        </HoverTip>
        <HoverTip
          wide
          block
          tip={(
            <div className="fin-refresh-tip">
              <strong>Average monthly burn</strong>
              <p className="fin-refresh-tip-lead">
                Total burn divided by {horizon}. This is a typical month of payroll and overhead, not the average of every dollar leaving the company.
              </p>
            </div>
          )}
        >
          <div className="fin-kpi-card">
            <span className="fin-kpi-label">Average monthly</span>
            <span className="fin-kpi-value">{formatCurrency(totals.avg)}</span>
            <span className="fin-kpi-sub">Across {horizon} forecast months.</span>
          </div>
        </HoverTip>
        <HoverTip
          wide
          block
          tip={(
            <div className="fin-refresh-tip">
              <strong>Peak monthly burn</strong>
              <p className="fin-refresh-tip-lead">
                {totals.peakLabel
                  ? `${totals.peakLabel} is the highest month of payroll and overhead in this window.`
                  : 'The highest month of payroll and overhead in this window.'}
                {' '}It is not the month with the most bills or project costs.
              </p>
            </div>
          )}
        >
          <div className="fin-kpi-card">
            <span className="fin-kpi-label">Peak monthly</span>
            <span className="fin-kpi-value">{formatCurrency(totals.max)}</span>
            <span className="fin-kpi-sub">Highest single month in window.</span>
          </div>
        </HoverTip>
        {hasActualsData && (
          <>
            <HoverTip
              wide
              block
              tip={(
                <div className="fin-refresh-tip">
                  <strong>Average actual revenue</strong>
                  <p className="fin-refresh-tip-lead">
                    QuickBooks revenue, averaged over the last {actualsAvg.months} months that have {method} numbers.
                    This is money already recorded, not the forecast.
                  </p>
                </div>
              )}
            >
              <div className="fin-kpi-card">
                <span className="fin-kpi-label">Avg actual revenue / mo</span>
                <span className="fin-kpi-value">{formatCurrency(actualsAvg.revenue)}</span>
                <span className="fin-kpi-sub">Last {actualsAvg.months} mo ({method}).</span>
              </div>
            </HoverTip>
            <HoverTip
              wide
              block
              tip={(
                <div className="fin-refresh-tip">
                  <strong>Average actual COGS</strong>
                  <p className="fin-refresh-tip-lead">
                    QuickBooks cost of goods sold for those same {actualsAvg.months} months, on the {method} books.
                    Timber, materials, and other direct job costs. Overhead and payroll stay in the burn cards.
                  </p>
                </div>
              )}
            >
              <div className="fin-kpi-card">
                <span className="fin-kpi-label">Avg actual COGS / mo</span>
                <span className="fin-kpi-value">{formatCurrency(actualsAvg.cogs)}</span>
                <span className="fin-kpi-sub">Direct job &amp; material costs from QB.</span>
              </div>
            </HoverTip>
          </>
        )}
        {hasCashBalanceData && dailyReport && (
          <HoverTip
            wide
            block
            tip={(
              <div className="fin-refresh-tip">
                <strong>Sequence cash</strong>
                <p className="fin-refresh-tip-lead">
                  Cash on hand in the Sequence pods, Payments, and the Chase Short Term Ops account
                  {dailyReport.dateKey ? ` as of ${formatDayKey(dailyReport.dateKey)}` : ''}.
                  The projected cash line on the chart starts from this balance.
                </p>
              </div>
            )}
          >
            <div className="fin-kpi-card">
              <span className="fin-kpi-label">Sequence cash</span>
              <span className="fin-kpi-value">{formatCurrency(dailyReport.sequencePodAmount)}</span>
              <span className="fin-kpi-sub">
                Pods, Payments, and Chase Short Term Ops · {dailyReport.dateKey || 'latest snapshot'}.
              </span>
            </div>
          </HoverTip>
        )}
      </div>

      <div className="fin-forecast-chart-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ marginRight: 'auto' }}>
            {showActuals && hasActualsData
              ? `Actuals & projected — next ${horizon} months`
              : `Projected cash — next ${horizon} months`}
          </h3>
          {zoomPreset !== 'month' && zoomRangeLabel && (
            <span className="fin-balance-meta">{zoomRangeLabel}</span>
          )}
          {earlyPayKeys.size > 0 && (
            <button
              type="button"
              className="fin-pay-now-btn is-on"
              onClick={() => setEarlyPayKeys(new Set())}
            >
              Clear pay now
            </button>
          )}
          <ForecastFlowFilterControl
            filter={flowFilter}
            onChange={setFlowFilter}
            projects={flowFilterProjects}
            onExportProject={displayProjection ? exportProjectCashflow : undefined}
            exportingKey={exportingCashflowKey}
          />
          <div className="fin-segmented" role="group" aria-label="Chart zoom">
            {([
              ['day', 'Day'],
              ['week', 'Week'],
              ['month', 'Month'],
            ] as Array<[ForecastZoomPreset, string]>).map(([preset, label]) => (
              <button
                key={preset}
                type="button"
                className={zoomPreset === preset ? 'active' : ''}
                onClick={() => applyZoomPreset(preset)}
                aria-pressed={zoomPreset === preset}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div
          ref={chartWrapRef}
          className="fin-forecast-chart-wrap"
          style={{ width: '100%', height: 360, minHeight: 360 }}
          onDoubleClick={resetZoom}
          title={zoomPreset === 'month'
            ? 'Day, Week, or Month · or scroll to zoom'
            : 'Scroll to zoom · horizontal scroll to pan · double-click for Month'}
        >
          <ResponsiveContainer width="100%" height="100%" minHeight={360}>
            {hasActualsData && showActuals ? (
              <ComposedChart
                data={chartData}
                margin={{ top: 10, right: CHART_RIGHT_MARGIN_PX, bottom: 0, left: 0 }}
                barGap={-flowBarSize}
                onClick={handleChartClick}
                accessibilityLayer={false}
              >
                <NegativeCashBand floor={yDomain?.[0] ?? 0} />
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  type="number"
                  dataKey="x"
                  domain={xDomain}
                  allowDataOverflow
                  ticks={xTicks}
                  tickFormatter={formatXTick}
                  tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
                  padding={{ left: 0, right: 0 }}
                />
                <YAxis
                  domain={yDomain}
                  allowDataOverflow
                  ticks={yTicks}
                  interval={yTicks ? 0 : undefined}
                  tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
                  tickFormatter={formatAxisCurrency}
                  width={CHART_Y_AXIS_PX}
                />
                <Tooltip
                  content={<ForecastChartTooltip formatLabel={formatXTick} historicHover={historicHover} />}
                  wrapperStyle={{ zIndex: 1200, pointerEvents: 'none' }}
                  allowEscapeViewBox={{ x: true, y: true }}
                  reverseDirection={{ y: true }}
                  offset={{ x: 12, y: 4 }}
                />
                <ForecastHoverCashLine />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line
                  type="monotone"
                  dataKey={ACTUAL_REVENUE_KEY}
                  stroke={ACTUAL_REVENUE_COLOR}
                  strokeWidth={1.6}
                  strokeDasharray="4 3"
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey={ACTUAL_COGS_KEY}
                  stroke={ACTUAL_COGS_COLOR}
                  strokeWidth={1.6}
                  strokeDasharray="4 3"
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey={ACTUAL_MARKETING_KEY}
                  stroke={ACTUAL_MARKETING_COLOR}
                  strokeWidth={1.6}
                  strokeDasharray="4 3"
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey={ACTUAL_OVERHEAD_KEY}
                  stroke={ACTUAL_OVERHEAD_COLOR}
                  strokeWidth={1.6}
                  strokeDasharray="4 3"
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
                {hasCashBalanceData && (
                  <Line
                    type="monotone"
                    dataKey={CASH_BALANCE_KEY}
                    name={CASH_BALANCE_KEY}
                    stroke={CASH_BALANCE_COLOR}
                    strokeWidth={2.2}
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                )}
                {projection && (
                  <Bar
                    dataKey={WF_AR_KEY}
                    name={WF_AR_KEY}
                    fill={WF_AR_COLOR}
                    fillOpacity={0.55}
                    barSize={flowBarSize}
                    legendType="square"
                    isAnimationActive={false}
                  />
                )}
                {projection && (
                  <Bar
                    dataKey={WF_AP_KEY}
                    name={WF_AP_KEY}
                    fill={WF_AP_COLOR}
                    fillOpacity={0.55}
                    barSize={flowBarSize}
                    legendType="square"
                    shape={ApOutBarWithWarnings}
                    isAnimationActive={false}
                  />
                )}
                {projection && chartGrain !== 'daily' && (
                  <Bar
                    dataKey={WF_BURN_KEY}
                    name={WF_BURN_KEY}
                    fill={WF_BURN_COLOR}
                    fillOpacity={0.5}
                    barSize={flowBarSize}
                    legendType="square"
                    shape={BurnBarWithArrow}
                    isAnimationActive={false}
                  />
                )}
                {projection && (
                  <Bar
                    dataKey={WF_PLANNED_KEY}
                    name={WF_PLANNED_KEY}
                    fill={WF_PLANNED_COLOR}
                    fillOpacity={0.55}
                    barSize={flowBarSize}
                    legendType="square"
                    isAnimationActive={false}
                  />
                )}
                {projection && (
                  <Bar
                    dataKey={WF_JOB_KEY}
                    name={WF_JOB_KEY}
                    fill={WF_JOB_COLOR}
                    fillOpacity={0.55}
                    barSize={flowBarSize}
                    legendType="square"
                    isAnimationActive={false}
                  />
                )}
                {projection && (
                  <Bar
                    dataKey={WF_WARN_KEY}
                    name={WF_WARN_KEY}
                    fill="none"
                    barSize={flowBarSize}
                    legendType="none"
                    shape={QuoteWarnMark}
                    minPointSize={1}
                    isAnimationActive={false}
                  />
                )}
                {projection && (
                  <Line
                    type="monotone"
                    dataKey={PROJECTED_CASH_KEY}
                    name={PROJECTED_CASH_KEY}
                    stroke={scenarioActive ? PROJECTED_CASH_GHOST : CASH_BALANCE_COLOR}
                    strokeWidth={scenarioActive ? 1.6 : 2.2}
                    strokeDasharray="6 4"
                    dot={false}
                    connectNulls
                    hide={overlayProjectedFromNow}
                    isAnimationActive={false}
                  />
                )}
                {scenarioActive && (
                  <Line
                    type="monotone"
                    dataKey={SCENARIO_CASH_KEY}
                    name={overlayChartName}
                    stroke={SCENARIO_CASH_COLOR}
                    strokeWidth={2.4}
                    dot={false}
                    connectNulls
                    hide={overlayProjectedFromNow}
                    isAnimationActive={false}
                  />
                )}
                <ForecastCashOverlays
                  historicPoints={historicalCashLineData}
                  projectedPoints={projectedCashFromNowPoints}
                  scenarioPoints={scenarioCashFromNowPoints}
                  showHistoric={showHistoricCashPath}
                  overlayProjected={overlayProjectedFromNow}
                  scenarioActive={scenarioActive}
                  onHistoricHover={onHistoricHover}
                />
                {currentMonthX != null && currentMonthX >= xDomain[0] && currentMonthX <= xDomain[1] && (
                  <ReferenceLine
                    x={currentMonthX}
                    stroke="#6B6960"
                    strokeDasharray="2 2"
                    label={{ value: 'Now', position: 'top', fill: 'var(--color-text-secondary)', fontSize: 10 }}
                  />
                )}
              </ComposedChart>
            ) : (
              <ComposedChart
                data={chartData}
                margin={{ top: 10, right: CHART_RIGHT_MARGIN_PX, bottom: 0, left: 0 }}
                barGap={-flowBarSize}
                onClick={handleChartClick}
                accessibilityLayer={false}
              >
                <NegativeCashBand floor={yDomain?.[0] ?? 0} />
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  type="number"
                  dataKey="x"
                  domain={xDomain}
                  allowDataOverflow
                  ticks={xTicks}
                  tickFormatter={formatXTick}
                  tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
                  padding={{ left: 0, right: 0 }}
                />
                <YAxis
                  domain={yDomain}
                  allowDataOverflow
                  ticks={yTicks}
                  interval={yTicks ? 0 : undefined}
                  tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
                  tickFormatter={formatAxisCurrency}
                  width={CHART_Y_AXIS_PX}
                />
                <Tooltip
                  content={<ForecastChartTooltip formatLabel={formatXTick} historicHover={historicHover} />}
                  wrapperStyle={{ zIndex: 1200, pointerEvents: 'none' }}
                  allowEscapeViewBox={{ x: true, y: true }}
                  reverseDirection={{ y: true }}
                  offset={{ x: 12, y: 4 }}
                />
                <ForecastHoverCashLine />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {hasCashBalanceData && (
                  <Line
                    type="monotone"
                    dataKey={CASH_BALANCE_KEY}
                    name={CASH_BALANCE_KEY}
                    stroke={CASH_BALANCE_COLOR}
                    strokeWidth={2.2}
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                )}
                {projection && (
                  <Bar
                    dataKey={WF_AR_KEY}
                    name={WF_AR_KEY}
                    fill={WF_AR_COLOR}
                    fillOpacity={0.55}
                    barSize={flowBarSize}
                    legendType="square"
                    isAnimationActive={false}
                  />
                )}
                {projection && (
                  <Bar
                    dataKey={WF_AP_KEY}
                    name={WF_AP_KEY}
                    fill={WF_AP_COLOR}
                    fillOpacity={0.55}
                    barSize={flowBarSize}
                    legendType="square"
                    shape={ApOutBarWithWarnings}
                    isAnimationActive={false}
                  />
                )}
                {projection && chartGrain !== 'daily' && (
                  <Bar
                    dataKey={WF_BURN_KEY}
                    name={WF_BURN_KEY}
                    fill={WF_BURN_COLOR}
                    fillOpacity={0.5}
                    barSize={flowBarSize}
                    legendType="square"
                    shape={BurnBarWithArrow}
                    isAnimationActive={false}
                  />
                )}
                {projection && (
                  <Bar
                    dataKey={WF_PLANNED_KEY}
                    name={WF_PLANNED_KEY}
                    fill={WF_PLANNED_COLOR}
                    fillOpacity={0.55}
                    barSize={flowBarSize}
                    legendType="square"
                    isAnimationActive={false}
                  />
                )}
                {projection && (
                  <Bar
                    dataKey={WF_JOB_KEY}
                    name={WF_JOB_KEY}
                    fill={WF_JOB_COLOR}
                    fillOpacity={0.55}
                    barSize={flowBarSize}
                    legendType="square"
                    isAnimationActive={false}
                  />
                )}
                {projection && (
                  <Bar
                    dataKey={WF_WARN_KEY}
                    name={WF_WARN_KEY}
                    fill="none"
                    barSize={flowBarSize}
                    legendType="none"
                    shape={QuoteWarnMark}
                    minPointSize={1}
                    isAnimationActive={false}
                  />
                )}
                {projection && (
                  <Line
                    type="monotone"
                    dataKey={PROJECTED_CASH_KEY}
                    name={PROJECTED_CASH_KEY}
                    stroke={scenarioActive ? PROJECTED_CASH_GHOST : CASH_BALANCE_COLOR}
                    strokeWidth={scenarioActive ? 1.6 : 2.2}
                    strokeDasharray="6 4"
                    dot={false}
                    connectNulls
                    hide={overlayProjectedFromNow}
                    isAnimationActive={false}
                  />
                )}
                {scenarioActive && (
                  <Line
                    type="monotone"
                    dataKey={SCENARIO_CASH_KEY}
                    name={overlayChartName}
                    stroke={SCENARIO_CASH_COLOR}
                    strokeWidth={2.4}
                    dot={false}
                    connectNulls
                    hide={overlayProjectedFromNow}
                    isAnimationActive={false}
                  />
                )}
                <ForecastCashOverlays
                  historicPoints={historicalCashLineData}
                  projectedPoints={projectedCashFromNowPoints}
                  scenarioPoints={scenarioCashFromNowPoints}
                  showHistoric={showHistoricCashPath}
                  overlayProjected={overlayProjectedFromNow}
                  scenarioActive={scenarioActive}
                  onHistoricHover={onHistoricHover}
                />
                {currentMonthX != null && currentMonthX >= xDomain[0] && currentMonthX <= xDomain[1] && (
                  <ReferenceLine
                    x={currentMonthX}
                    stroke="#6B6960"
                    strokeDasharray="2 2"
                    label={{ value: 'Now', position: 'top', fill: 'var(--color-text-secondary)', fontSize: 10 }}
                  />
                )}
              </ComposedChart>
            )}
          </ResponsiveContainer>
        </div>
        {historicHover && (
          <div
            ref={historicTipRef}
            className="fin-forecast-chart-tooltip"
            style={{
              position: 'fixed',
              left: historicHover.clientX + 14,
              top: historicHover.clientY,
              transform: 'translateY(calc(-100% - 12px))',
              pointerEvents: 'none',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{historicHover.label}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: CASH_BALANCE_COLOR }}>
              Actual cash: {formatCurrency(historicHover.cash)}
            </div>
          </div>
        )}
      </div>

      {(inspectedSlice || inspectedMonth) && (
        <div className="fin-proj-detail-card" ref={detailCardRef}>
          <div className="fin-proj-detail-head">
            <h3>
              {inspectedDay
                ? `${inspectedDay.day.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} — projected cash detail`
                : inspectedWeek
                  ? `Week of ${inspectedWeek.weekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} — projected cash detail`
                  : `${inspectedMonth!.month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} — projected cash detail`}
            </h3>
            <button
              type="button"
              className="fin-secondary-btn"
              onClick={() => {
                setInspectedMonthKey(null);
                setInspectedDayKey(null);
                setInspectedWeekKey(null);
              }}
              aria-label={inspectedDay ? 'Close day detail' : inspectedWeek ? 'Close week detail' : 'Close month detail'}
            >
              Close
            </button>
          </div>
          <div className="fin-proj-detail-cols fin-proj-detail-cols--split">
            <div className="fin-proj-detail-col">
              <div className="fin-proj-detail-col-head fin-proj-detail-col-head--in">
                Money in · {formatCurrency(inspectedSlice ? inspectedSliceVisible.arIn : inspectedMonthVisible.arIn)}
              </div>
              <div className="fin-proj-detail-col-body">
              {(inspectedSlice ? inspectedDayInGroups : inspectedInGroups).length === 0 && (
                <div className="fin-proj-detail-empty">
                  {inspectedDay
                    ? 'No client payments expected this day.'
                    : inspectedWeek
                      ? 'No client payments expected this week.'
                      : 'No client payments expected this month.'}
                </div>
              )}
              {(inspectedSlice ? inspectedDayInGroups : inspectedInGroups).map((group) => (
                <ForecastProjectGroupCard
                  key={`in-${group.key}`}
                  group={group}
                  direction="in"
                  expanded={expandedDetailGroups.has(`in:${group.key}`)}
                  onToggle={() => toggleDetailGroup(`in:${group.key}`)}
                  billsById={billsById}
                  supplierTaskEndDates={supplierTaskEndDates}
                  billHasPreview={billHasPreview}
                  onPreviewHover={(hovered) => {
                    previewHoverRef.current = hovered;
                  }}
                  canViewObligations={canViewObligations}
                />
              ))}
              </div>
            </div>
            <div className="fin-proj-detail-col">
              <div className="fin-proj-detail-col-head fin-proj-detail-col-head--out">
                <span>Money out · {formatCurrency(moneyOutDisplayedTotal)}</span>
                <MoneyOutKindFilter
                  tags={moneyOutTags}
                  present={moneyOutTagsPresent}
                  onChange={setMoneyOutTags}
                />
              </div>
              <div className="fin-proj-detail-col-body">
              <ForecastMoneyOutEntries
                sections={inspectedSlice ? inspectedDayOutSections : inspectedOutSections}
                flat={moneyOutKindFilterActive(moneyOutTags)}
                expandedDetailGroups={expandedDetailGroups}
                onToggleGroup={toggleDetailGroup}
                billsById={billsById}
                supplierTaskEndDates={supplierTaskEndDates}
                billHasPreview={billHasPreview}
                onPreviewHover={(hovered) => {
                  previewHoverRef.current = hovered;
                }}
                canViewObligations={canViewObligations}
                onOpenBurn={openBurnDetail}
                onOpenCockpit={openCockpit}
                payNowToday={forecastToday}
                onTogglePayNow={togglePayNow}
              />
              </div>
            </div>
          </div>
          <div className="fin-proj-detail-footer">
            {inspectedSlice ? (
              <>
                +{formatCurrency(inspectedSlice.arIn)} AR − {formatCurrency(inspectedSlice.apOut)} AP −{' '}
                {formatCurrency(inspectedSlice.burn)} burn − {formatCurrency(inspectedSlice.plannedOut)} planned −{' '}
                {formatCurrency(inspectedSlice.jobOut)} job ={' '}
                {(() => {
                  const net = inspectedSlice.arIn - inspectedSlice.apOut - inspectedSlice.burn
                    - inspectedSlice.plannedOut - inspectedSlice.jobOut;
                  return (
                    <>
                      {net < 0 ? '−' : '+'}
                      {formatCurrency(Math.abs(net))} net → cash{' '}
                      <strong>{formatCurrency(inspectedSlice.projectedCash)}</strong>
                    </>
                  );
                })()}
              </>
            ) : (
              <>
                +{formatCurrency(inspectedMonth!.arIn)} AR − {formatCurrency(inspectedMonth!.apOut)} AP −{' '}
                {formatCurrency(inspectedMonth!.burn)} burn − {formatCurrency(inspectedMonth!.plannedOut)} planned −{' '}
                {formatCurrency(inspectedMonth!.jobOut || 0)} job ={' '}
                {inspectedMonth!.net < 0 ? '−' : '+'}
                {formatCurrency(Math.abs(inspectedMonth!.net))} net → ending balance{' '}
                <strong>{formatCurrency(inspectedMonth!.endingBalance)}</strong>
              </>
            )}
          </div>
        </div>
      )}

      <div className={`fin-forecast-table-card${numbersTableOpen ? '' : ' is-collapsed'}`} ref={numbersTableRef}>
        <button
          type="button"
          className="fin-forecast-table-toggle"
          aria-expanded={numbersTableOpen}
          onClick={() => setNumbersTableOpen((open) => !open)}
        >
          <ChevronRight size={14} className={numbersTableOpen ? 'is-open' : ''} aria-hidden />
          <span className="fin-forecast-table-toggle-title">Numbers table</span>
          <span className="fin-forecast-table-toggle-meta">
            {numbersTableOpen ? 'Hide monthly actuals' : 'Show monthly actuals'}
          </span>
        </button>
        {numbersTableOpen && (
        <div className="fin-forecast-table-scroll">
          <table className="fin-forecast-table" aria-label="Monthly forecast table">
            <thead>
              <tr>
                <th>Month</th>
                {hasActualsData && showActuals && (
                  <>
                    <th className="right">Actual revenue</th>
                    <th className="right">Actual COGS</th>
                    <th className="right">Actual marketing</th>
                    <th className="right">Actual overhead</th>
                  </>
                )}
                {hasCashBalanceData && <th className="right">Cash balance</th>}
                <th className="right">Overhead</th>
                <th className="right">Payroll</th>
                <th className="right">Total</th>
              </tr>
            </thead>
            <tbody>
              {hasActualsData && showActuals && timeline
                .filter(t => t.isPast)
                .map(t => {
                  const p = overlayByMonthKey.get(t.monthKey);
                  const cash = cashBalanceByMonthKey[t.monthKey];
                  return (
                <tr key={`overlay-${t.monthKey}`} style={{ color: '#6B6960' }}>
                  <td>{t.label}</td>
                  <td className="right" style={{ color: p ? ACTUAL_REVENUE_COLOR : undefined }}>
                    {p ? formatCurrency(p.actualRevenue) : '—'}
                  </td>
                  <td className="right" style={{ color: p ? ACTUAL_COGS_COLOR : undefined }}>
                    {p ? formatCurrency(p.actualCogs) : '—'}
                  </td>
                  <td className="right" style={{ color: p ? ACTUAL_MARKETING_COLOR : undefined }}>
                    {p ? formatCurrency(p.actualMarketing) : '—'}
                  </td>
                  <td className="right" style={{ color: p ? ACTUAL_OVERHEAD_COLOR : undefined }}>
                    {p ? formatCurrency(p.actualOverhead) : '—'}
                  </td>
                  {hasCashBalanceData && (
                    <td className="right" style={{ color: cash ? CASH_BALANCE_COLOR : undefined }}>
                      {cash ? formatCurrency(cash.balance) : '—'}
                    </td>
                  )}
                  <td className="right">—</td>
                  <td className="right">—</td>
                  <td className="right">—</td>
                </tr>
              );})}
              {buckets.map(b => {
                const overlay = overlayByLabel.get(b.label);
                const cash = cashBalanceByMonthKey[toMonthKey(b.month)];
                return (
                <tr key={b.label}>
                  <td>
                    {b.label}
                    {overlay?.isCurrentMonth && (
                      <span className="fin-peremp-badge" style={{ marginLeft: 6 }}>MTD</span>
                    )}
                  </td>
                  {hasActualsData && showActuals && (
                    <>
                      <td className="right" style={{ color: overlay ? ACTUAL_REVENUE_COLOR : undefined }}>
                        {overlay ? formatCurrency(overlay.actualRevenue) : '—'}
                      </td>
                      <td className="right" style={{ color: overlay ? ACTUAL_COGS_COLOR : undefined }}>
                        {overlay ? formatCurrency(overlay.actualCogs) : '—'}
                      </td>
                      <td className="right" style={{ color: overlay ? ACTUAL_MARKETING_COLOR : undefined }}>
                        {overlay ? formatCurrency(overlay.actualMarketing) : '—'}
                      </td>
                      <td className="right" style={{ color: overlay ? ACTUAL_OVERHEAD_COLOR : undefined }}>
                        {overlay ? formatCurrency(overlay.actualOverhead) : '—'}
                      </td>
                    </>
                  )}
                  {hasCashBalanceData && (
                    <td className="right" style={{ color: cash ? CASH_BALANCE_COLOR : undefined }}>
                      {cash ? formatCurrency(cash.balance) : '—'}
                    </td>
                  )}
                  <td
                    className="right"
                    title={`Overhead ${formatCurrency(b.recurringOverheadTotal)} · Budgeted ${formatCurrency(b.budgetedOpexTotal)}`}
                  >
                    {formatCurrency(b.overheadTotal)}
                  </td>
                  <td className="right">{formatCurrency(b.payrollTotal)}</td>
                  <td className="right" style={{ fontWeight: 700 }}>{formatCurrency(b.total)}</td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {(quickLook || quickLookLoading) && (
        <div
          className="bp-quicklook-overlay"
          onClick={() => {
            setQuickLook(null);
            setQuickLookLoading(false);
          }}
        >
          {quickLook ? (
            <div
              className={`bp-quicklook${quickLook.kind === 'summary' ? ' bp-quicklook--summary' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <header className="bp-quicklook-head">
                <span className="bp-quicklook-name" title={quickLook.name}>
                  {quickLook.name}
                </span>
                <span className="bp-quicklook-hint">Space or Esc to close</span>
                {quickLook.kind === 'file' && (
                  <a
                    className="bp-quicklook-open"
                    href={quickLook.url}
                    target="_blank"
                    rel="noreferrer"
                    title="Open in a new tab"
                  >
                    <ExternalLink size={14} />
                  </a>
                )}
                <button
                  type="button"
                  className="bp-quicklook-close"
                  onClick={() => setQuickLook(null)}
                  aria-label="Close preview"
                >
                  <X size={16} />
                </button>
              </header>
              {quickLook.kind === 'file' ? (
                <iframe className="bp-quicklook-frame" src={quickLook.url} title={quickLook.name} />
              ) : (
                <div className="bp-quicklook-summary">
                  {quickLook.lines.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <Loader2 size={30} className="bp-spin bp-quicklook-spinner" />
          )}
        </div>
      )}

      {cockpitOpen && (
        <CockpitModal
          onClose={() => {
            setCockpitOpen(false);
            setBonusDraft(null);
            setTaxDraft(null);
            setSettingsDraft(null);
          }}
          projection={displayProjection ?? projection}
          dailyReport={dailyReport}
          overhead={overhead}
          positions={positions}
          taxBreakdown={taxComputed.breakdown}
          bonusComputed={canViewObligations ? bonusComputed : null}
          actorUserId={user?.id}
          canWrite={canWrite}
          canViewObligations={canViewObligations}
          profitInputs={{
            qbMonths: activeMethodData?.months,
            qbTotals: activeMethodData?.totals,
            moneyInRows,
            buckets,
            deals: enhancedDeals,
            salesOrders,
            changeOrders,
            projectionMonths: (bakedProjection ?? projection)?.months,
          }}
          obligationPanel={
            canViewObligations && bonusDraft && taxDraft
              ? {
                year: obligationYear,
                years: obligationYears,
                onYearChange: (y) => {
                  setObligationYear(y);
                  setBonusDraft(obligations.bonusFor(y));
                  setTaxDraft(obligations.taxFor(y));
                },
                bonusPlan: bonusDraft,
                onBonusChange: setBonusDraft,
                bonusComputed,
                taxPlan: taxDraft,
                onTaxChange: setTaxDraft,
                taxSettings: settingsDraft ?? obligations.settings,
                onTaxSettingsChange: setSettingsDraft,
                taxComputed,
                estimatedProfit: taxDraft.estimatedAnnualProfit,
                onProfitChange: (profit) => {
                  setTaxDraft({ ...taxDraft, estimatedAnnualProfit: profit });
                },
                profitHint,
                baselineLowest: savedProjection?.lowestBalance ?? null,
                draftLowest: projection?.lowestBalance ?? null,
                busy: obligations.busy,
                error: obligations.error,
                onSaveBonus: () => {
                  void (async () => {
                    await obligations.persistTax(taxDraft);
                    await obligations.persistBonus(bonusDraft);
                  })();
                },
                onSaveTax: () => {
                  void (async () => {
                    if (settingsDraft) await obligations.persistSettings(settingsDraft);
                    await obligations.persistTax(taxDraft);
                  })();
                },
              }
              : null
          }
          horizon={horizon}
          billPayClearing={billPayClearing}
          onBillPayClearingChange={setBillPayClearing}
          onSaveBillPayClearing={() =>
            saveBillPayClearingSettings(billPayClearing, user?.id)
          }
        />
      )}

      {planningOpen && projection && (
        <CashPlanningModal
          baseline={bakedProjection ?? projection}
          lines={scenarioLines}
          onChange={setScenarioLines}
          onClose={() => {
            void persistPlan({ quiet: true });
            setPlanningOpen(false);
          }}
          title={planTitle}
          why={planWhy}
          onTitleChange={setPlanTitle}
          onWhyChange={setPlanWhy}
          plans={workspace.plans}
          activePlan={workspace.activePlan}
          onOpenPlan={openSavedPlan}
          onNewPlan={startUnsavedPlan}
          onDeletePlan={deleteSavedPlan}
          onShare={() => { void persistPlan({ share: true }); }}
          onSaveAs={persistPlanAs}
          onStatus={workspace.setStatus}
          saveState={planSaveState}
          comments={workspace.comments}
          onComment={workspace.postComment}
          canWrite={canWrite}
          canComment={canComment}
          mentionableUsers={workspace.mentionableUsers}
          stackedPlanIds={stackedPlanIds}
          onToggleStacked={toggleStackedPlan}
          canDecide={workspace.canDecide}
          busy={workspace.busy}
          error={workspace.error}
          supplierBills={supplierBills}
          projectNameById={projectNameById}
          projectIdByDealId={projectIdByDealId}
          supplierTaskEndDates={supplierTaskEndDates}
          taskNameById={taskNameById}
          onEnsurePlanId={() => persistPlan({ quiet: true })}
        />
      )}
    </div>
    </ForecastBillMetaContext.Provider>
  );
};

export default ForecastView;
